import { makeAutoObservable, runInAction, when } from "mobx";
import RootStore from "stores/RootStore";
import { mmErrorMessage } from "./mmErrorMessage";
import type {
  TicketPageImage,
  TicketPdfSource,
} from "views/millermize/Production/ticketPdf";
import {
  MM_TICKET_EXTRACT_CONTRACT_VERSION,
  REVIEW_CONFIDENCE_THRESHOLD,
  formatPageRange,
  type GroupedMessage,
  type TicketExtractMessage,
} from "views/millermize/Production/ticketExtractContract";

// ============================================================================
// TYPES
// ============================================================================

export type FormStatus = "pending" | "in_progress" | "completed";

export const FORM_BUILDERS = ["Jose", "Juan", "Jorge", "Other"] as const;
export type MarkProductType = "precast" | "vdt";

// The API's DATE columns come back as full ISO datetimes
// (e.g. "2026-06-24T00:00:00.000Z"). The backend validates writes as
// `format: 'date'` (yyyy-MM-dd), so a re-sent datetime fails validation on
// patch. Normalize to a plain date string everywhere we read/write these.
// Anything that is not yyyy-MM-dd after the split is dropped rather than
// round-tripped: a native date input renders a malformed value as blank, so
// keeping it would fail the required-field check silently on the next save.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Mark numbers sort alphabetically then numerically (A-2 before A-13). Hoisted
// because `localeCompare` with options allocates a collator per comparison,
// which is ~12x the cost of a shared one when the schedule and form maps sort
// thousands of marks.
const markNumberOrder = new Intl.Collator(undefined, { numeric: true });

// Same collation, for view code that orders the tenant's human-readable
// identifiers — mark numbers and form numbers both need "F2" before "F10".
export const compareNaturally = (a: string, b: string): number =>
  markNumberOrder.compare(a, b);

/**
 * The order ticket groups read in, on this screen and in the unscheduled panel.
 *
 * BY CREATION ORDER (`sortOrder`), reversing the name ordering that shipped on
 * 2026-09-02 in `cfc4815`. Creede saw that release and asked for creation order
 * back: "that's why I put G3, G4, because I'm making it sort it by that number"
 * -- he had been prefixing names purely to control the order, and the name sort
 * turned his workaround into the mechanism.
 *
 * `sortOrder` is `max + 1` at create (TicketGroupDialog) and is never changed
 * afterwards, so this is genuinely "the order I made them in".
 *
 * KNOWN COST, stated so nobody rediscovers it as a bug: a group added
 * retroactively lands at the BOTTOM, which is exactly what cfc4815 existed to
 * fix. There is no reorder UI, so creation order cannot be corrected after the
 * fact. The real answer is a drag-to-reorder control (or an editable sort
 * field), which would let him drop the naming workaround entirely -- offered at
 * the review rather than assumed here.
 *
 * Ticket date, then name, break a tie, so the order is TOTAL: two groups
 * sharing a sortOrder would otherwise sort differently run to run, and a list
 * that reshuffles on reload is its own bug. A tied group with no ticket date
 * sorts LAST of those -- "" compares below every real date, which would
 * otherwise float the least complete record to the top.
 */
export const compareTicketGroups = (
  a: { ticketDate?: string; sortOrder: number; name: string },
  b: { ticketDate?: string; sortOrder: number; name: string }
): number => {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  // The date is a TIE-BREAK, and since 2026-09-15 a new group has none: the
  // date lives on the mark now. Groups created before that keep theirs, so this
  // still separates two of them that somehow share a sortOrder, and an undated
  // group sorts last among equals rather than first -- which is what "\uffff"
  // has always meant here.
  const dateA = a.ticketDate || "\uffff";
  const dateB = b.ticketDate || "\uffff";
  if (dateA !== dateB) return dateA < dateB ? -1 : 1;
  return compareNaturally(a.name, b.name);
};

// A form group shown the way the yard reads it: first form, dash, last form.
// Creede, on the pallet mark list: "you could just put the first and the last
// form number in that form group and with a dash between them. And I know it's
// form X through form Y." A single-form group is just that form, with no dash.
export const formRange = (forms: string[]): string => {
  if (forms.length === 0) return "";
  const sorted = [...forms].sort(compareNaturally);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return first === last ? first : `${first}-${last}`;
};

// ============================================================================
// BULK MARK ENTRY
//
// Excel currently beats this system at the client's highest-volume task. Creede
// hand-enters 400-mark jobs one modal at a time -- "400 marks in this job... and
// that's not necessarily uncommon at all" -- and was blunt about why he keeps
// the spreadsheet: "with Excel's kind of autofill and things like that, it's a
// little quicker than y'all's system, with me being able to go type WC01 and
// drag down for every WC because it'll go WC02, WC03."
//
// So: paste a block straight out of Excel. Three columns, which are both the
// three NOT NULL mark columns and exactly the spec he narrowed PDF import to --
// "the mark number and the quantity and the cubic foot... Y'all already have the
// logic built in to math all this." Everything else is derived.
//
// This is deliberately independent of PDF import (blocked on a parsing backend,
// third cycle). The spreadsheet is what he uses TODAY.
// ============================================================================

export interface ParsedMarkRow {
  markNumber: string;
  quantity: number;
  cubicFeetPerPiece: number;
  // Provenance, set only when the row was READ off a ticket PDF rather than
  // typed or pasted. Optional so both paths share one type and one review
  // grid: a pasted row is simply a row with nothing to be unsure about.
  /** 0..1 reader confidence. Below REVIEW_CONFIDENCE_THRESHOLD the grid marks
   *  it "check". Never used to drop a row -- only to draw attention to one. */
  confidence?: number;
  /** 1-based page it was read from, so a reviewer can find it in the PDF. */
  sourcePage?: number;
  /**
   * Every page this mark was read from, once continuation pages have been
   * folded together. One entry in the normal case; two or more when a ticket
   * ran over a page break. Set only by the PDF path.
   */
  sourcePages?: number[];
  /**
   * The date printed on the ticket this mark was read from (yyyy-MM-dd).
   *
   * Per MARK, not per ticket group, because marks join a group late and the
   * group has to stay whole when they do -- Creede, 2026-09-15: "say we had
   * something added on the first, it'd get added in right here with that one
   * ticket that changed, but this whole group remains together."
   */
  ticketDate?: string;
}

export interface MarkPasteResult {
  rows: ParsedMarkRow[];
  /** One message per unusable line, with its 1-based number. Never silent. */
  errors: string[];
  /**
   * Marks that appeared on more than one page and were counted ONCE. Not a
   * problem -- it is what Creede said to expect -- so it is reported apart
   * from `errors`, which is for things that need fixing.
   */
  continued?: string[];
  /**
   * Every distinct CAUSE the reader reported, with the pages it happened on and
   * whether it needs fixing. One cause on sixty pages is one entry here.
   *
   * `errors` above is kept as the flat list the paste path produces; the PDF
   * path fills BOTH, so nothing that reads `errors` breaks while the dialog
   * reads this.
   */
  messages?: GroupedMessage[];
}

// Excel copies as TAB-separated; a CSV paste arrives comma-separated. Accept
// either — but NOT both at once, because the comma is also a thousands
// separator and "WC01\t1,200\t12.5" would otherwise split into four fields and
// read the quantity as 1.
//
// So the delimiter is decided per line: a line containing a tab is a
// spreadsheet copy and splits ONLY on tabs, which lets thousands separators
// through untouched. A line without one falls back to commas or a run of
// spaces. In a comma-separated paste "1,200" is genuinely ambiguous and comes
// out as a column-count error, which is reported rather than silently
// misparsed.
const splitPastedRow = (line: string): string[] =>
  line.includes("\t") ? line.split("\t") : line.split(/,|\s{2,}/);

// "1,234.5" and "12.5 cf" both have to survive: the client's spreadsheets carry
// thousands separators and the odd unit.
const toNumber = (raw: string): number =>
  Number(String(raw).replace(/[^0-9.\-]/g, ""));

/**
 * Parse a pasted block into mark rows. Bad lines are REPORTED, never dropped --
 * silently ignoring a line in a 400-row paste is how a job ends up short a mark
 * with nobody the wiser.
 */
export const parseMarkPaste = (text: string): MarkPasteResult => {
  const rows: ParsedMarkRow[] = [];
  const errors: string[] = [];

  text.split(/\r?\n/).forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) return; // blank lines are padding, not errors

    const tabbed = line.includes("\t");
    const parts = splitPastedRow(line).map((part) => part.trim());
    const [markNumber, quantityRaw, cubicFeetRaw] = parts;

    // A tab paste may legitimately be wider than three columns — the client's
    // tickets also carry total cubic foot and pounds, and Creede said those are
    // derivable ("Y'all already have the logic built in to math all this"), so
    // extra columns are simply ignored.
    //
    // A COMMA paste cannot be treated the same way: four fields might be four
    // columns, or three columns where one held "1,200". Guessing would read a
    // 1,200-piece quantity as 1. Say so instead.
    if (!tabbed && parts.length > 3) {
      errors.push(
        `Line ${index + 1}: too many comma-separated values to read safely — a thousands separator looks like another column. Copy from Excel (tab-separated), or remove the commas inside numbers.`
      );
      return;
    }

    // A pasted header row is expected, not an error -- he will copy the
    // column titles as often as not.
    if (
      index === 0 &&
      markNumber &&
      /^mark/i.test(markNumber) &&
      !Number.isFinite(toNumber(quantityRaw ?? ""))
    ) {
      return;
    }

    if (parts.length < 3) {
      errors.push(
        `Line ${index + 1}: expected mark number, quantity and cubic feet — found ${parts.length} value(s)`
      );
      return;
    }
    if (!markNumber) {
      errors.push(`Line ${index + 1}: no mark number`);
      return;
    }

    const quantity = toNumber(quantityRaw);
    const cubicFeetPerPiece = toNumber(cubicFeetRaw);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      errors.push(`Line ${index + 1} (${markNumber}): quantity "${quantityRaw}" is not a positive number`);
      return;
    }
    if (!Number.isFinite(cubicFeetPerPiece) || cubicFeetPerPiece <= 0) {
      errors.push(`Line ${index + 1} (${markNumber}): cubic feet "${cubicFeetRaw}" is not a positive number`);
      return;
    }

    rows.push({ markNumber, quantity, cubicFeetPerPiece });
  });

  return { rows, errors };
};

/**
 * "4", "4-6", "4, 9" -- which page(s) of the packet a row came from.
 *
 * Lives here rather than in the dialog because it is now persisted as well as
 * displayed: a mark's `sourcePages` and the page span shown in the review grid
 * must be the same string, or the thing he uses to find the row in the PDF
 * disagrees with the thing stored on it.
 */
/** The note row as the API returns it, before it becomes an MMNote. Typed the
 *  way RawScheduleAssignment is, rather than `any`. */
interface RawNote {
  noteId?: string;
  id?: string;
  scope: MMNote["scope"];
  projectId?: string | null;
  ticketGroupId?: string | null;
  productionItemId?: string | null;
  body?: string | null;
  author?: string | null;
  kind?: string | null;
  pieces?: number | string | null;
  occurredOn?: string | null;
  createdAt?: string | null;
}

/** One place the API row becomes an MMNote, used by load and by create. */
const mapNote = (n: RawNote): MMNote => ({
  id: (n.noteId ?? n.id) as string,
  scope: n.scope,
  projectId: n.projectId || undefined,
  ticketGroupId: n.ticketGroupId || undefined,
  productionItemId: n.productionItemId || undefined,
  body: n.body ?? "",
  author: n.author || undefined,
  kind: n.kind === "recast" ? "recast" : "note",
  pieces: n.pieces == null ? undefined : Number(n.pieces),
  occurredOn: n.occurredOn ? toDateOnly(n.occurredOn) : undefined,
  createdAt: n.createdAt || undefined,
});

export const pageLabelFor = (row: {
  sourcePage?: number;
  sourcePages?: number[];
}): string | undefined => {
  const pages = row.sourcePages?.length
    ? row.sourcePages
    : row.sourcePage != null
    ? [row.sourcePage]
    : [];
  if (pages.length === 0) return undefined;
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  if (sorted.length === 1) return String(sorted[0]);
  const consecutive = sorted.every(
    (page, index) => index === 0 || page === sorted[index - 1] + 1
  );
  return consecutive
    ? `${sorted[0]}\u2013${sorted[sorted.length - 1]}`
    : sorted.join(", ");
};

/** Case- and space-insensitive mark identity, matching the duplicate check the
 *  bulk dialog already runs against the marks on a job. */
const markKey = (markNumber: string): string => markNumber.trim().toUpperCase();

/**
 * Fold a ticket that ran over a page break back into one mark per row.
 *
 * Creede, 2026-09-02: production tickets "have multiple pages for certain
 * marks... they do NOT repeat marks". So a mark number seen on two pages of one
 * PDF is ONE mark seen twice, not two marks -- and the reader, which sees each
 * page in isolation, will happily return it twice. Left alone that doubles the
 * quantity of every mark whose ticket spilled onto a second sheet: the job gets
 * cast twice and nobody finds out until the yard is full.
 *
 * The prompt now tells the model about continuation pages and names the marks
 * already read (see mm-ticket-extract.class.ts). This is the belt to that
 * braces, because the cost of one leaked duplicate is a re-pour.
 *
 * Two repeats of the same mark are treated differently by WHERE they are:
 *
 *   - ON DIFFERENT PAGES -- the continuation case. Counted once, pages recorded
 *     on the surviving row, and reported through `continued` rather than
 *     `errors`: this is the expected shape of the client's paperwork, not a
 *     fault to be fixed.
 *   - ON THE SAME PAGE -- a genuine duplicate on one table, which the prompt
 *     deliberately returns as two rows. Left alone; the dialog's existing
 *     duplicate check is what surfaces it.
 *
 * A cross-page repeat whose NUMBERS disagree is the one loud case. One of the
 * two readings is wrong and this function cannot know which, so it keeps the
 * more confident row, forces it below the review threshold so the grid marks it
 * "check", and states BOTH readings -- the discarded one is never silent.
 */
export const mergeContinuationRows = (rows: ParsedMarkRow[]): MarkPasteResult => {
  const out: ParsedMarkRow[] = [];
  const errors: string[] = [];
  const continued: string[] = [];
  // Mark -> index of the row in `out` that owns it. Only the FIRST occurrence
  // claims the key, so a same-page duplicate passes through without becoming a
  // merge target for a later page.
  const owner = new Map<string, number>();

  rows.forEach((row) => {
    const key = markKey(row.markNumber);
    const index = owner.get(key);

    if (index === undefined) {
      owner.set(key, out.length);
      out.push({
        ...row,
        sourcePages: row.sourcePage != null ? [row.sourcePage] : undefined,
      });
      return;
    }

    const kept = out[index];
    const samePage =
      row.sourcePage != null &&
      kept.sourcePages?.includes(row.sourcePage) === true;

    // Two rows for one mark on ONE page is what the prompt asks for and what
    // the duplicate check is there to show. Not a continuation.
    if (samePage) {
      out.push({ ...row, sourcePages: row.sourcePage != null ? [row.sourcePage] : undefined });
      return;
    }

    if (row.sourcePage != null) {
      kept.sourcePages = [...(kept.sourcePages ?? []), row.sourcePage];
    }

    const agrees =
      row.quantity === kept.quantity &&
      row.cubicFeetPerPiece === kept.cubicFeetPerPiece;

    if (agrees) {
      continued.push(row.markNumber);
      // The reader's own doubt travels with the mark: two pages agreeing does
      // not make a badly-plotted number legible.
      if (
        typeof row.confidence === "number" &&
        (typeof kept.confidence !== "number" || row.confidence < kept.confidence)
      ) {
        kept.confidence = row.confidence;
      }
      return;
    }

    // Disagreement. One of the two readings is wrong and this cannot know
    // which, so keep the more confident one, flag it, and state BOTH -- a
    // quantity silently halved is the same class of bug as a dropped row.
    const keptScore = typeof kept.confidence === "number" ? kept.confidence : 1;
    const rowScore = typeof row.confidence === "number" ? row.confidence : 1;

    const asRead = (
      source: ParsedMarkRow,
      page: number | undefined
    ): string =>
      `${page != null ? `page ${page}` : "another page"}: ${source.quantity} pc at ${source.cubicFeetPerPiece} cu.ft`;

    // Snapshot before anything is overwritten, so the message pairs each
    // reading with the page it actually came from.
    const first = asRead(kept, kept.sourcePage);
    const later = asRead(row, row.sourcePage);
    const rowWins = rowScore > keptScore;

    if (rowWins) {
      kept.quantity = row.quantity;
      kept.cubicFeetPerPiece = row.cubicFeetPerPiece;
    }
    // Forced under the threshold so the grid marks it "check". A row two pages
    // disagree about is exactly what a human should look at.
    kept.confidence = Math.min(
      keptScore,
      rowScore,
      REVIEW_CONFIDENCE_THRESHOLD - 0.01
    );

    errors.push(
      `${kept.markNumber} was read differently on two pages — ${first}, and ${later}. A mark is stated only once, so the ${
        rowWins ? "second" : "first"
      } reading was kept and the row is marked "check" — compare it with the PDF.`
    );
  });

  return { rows: out, errors, continued };
};

// Identity of a form for the one-piece-per-form-per-day rule. Case- and
// space-insensitive because real form numbers are not typed consistently — the
// client's own data carries `f3` next to `F3`, and `F3a` next to `F3A`.
//
// An empty key means "no form recorded", which is 32 of 72 marks in the live
// data. Those cannot participate in the rule: two formless placements are not
// known to share a form, so grouping them would cascade unrelated pieces, and
// blocking them would forbid a legal drop. Callers must treat "" as opt-out.
export const formKey = (formId?: string | null): string =>
  (formId ?? "").trim().toUpperCase();

/**
 * One spelling per phase name (2026-10-16 item 25). Creede: "The
 * capitalization of that drives Stephen nuts… automatically forced to be
 * capital P… and rectify." "pHASE 1" -> "Phase 1"; "MU" / "mock up" ->
 * "Mock-up" (the same /^(mu|mock)/i that decides a mock-up everywhere else);
 * anything else as typed. The API applies the same rule on every write
 * (packages/api/src/services/miller-mize/phase.ts) -- change both together.
 */
export const normalisePhase = (value: string): string => {
  const trimmed = value.trim().replace(/\s+/g, " ");
  const numbered = /^phase\s*(\d+)$/i.exec(trimmed);
  if (numbered) return `Phase ${Number(numbered[1])}`;
  if (/^(mu|mock)/i.test(trimmed)) return "Mock-up";
  return trimmed;
};

const withNormalisedPhase = <T extends { phase?: string | null }>(data: T): T =>
  typeof data.phase === "string" ? { ...data, phase: normalisePhase(data.phase) } : data;

// A job produced in stages restarts its form numbers each phase, and the new
// number is NEW STEEL (Sherman, 2026-08-31), so a form is identified by
// (project, phase, number) — see migrations/20260831090000_mm_job_phases.ts.
// Empty means the job's only phase, which is every job that has none.
export const phaseKey = (phase?: string | null): string =>
  (phase ?? "").trim().toUpperCase();

// Does a form RECORD belong to the phase being asked about? A form that names
// a phase is only that phase's steel. A form that names NONE belongs to every
// phase of its job: blank does not mean "phase 1", it means nobody has said,
// which is true of every form built before the phases migration (it backfilled
// nothing) and of any form built while the header sits on "All Phases".
//
// Only ever compare a form record against a REQUESTED phase with this. Two
// placements are compared with each other through formIdentity, where both
// phases come from ticket groups and blank really does mean "no phase".
export const formInPhase = (
  formPhase: string | null | undefined,
  phase: string | null | undefined
): boolean => {
  const key = phaseKey(formPhase);
  return key === "" || key === phaseKey(phase);
};

// The identity two placements must share to be "the same form". Empty when
// there is no form number, which never collides with anything.
export const formIdentity = (
  formId: string | null | undefined,
  phase: string | null | undefined
): string => {
  const key = formKey(formId);
  return key ? `${phaseKey(phase)}\u0000${key}` : "";
};

const toDateOnly = (value: unknown): string => {
  if (value == null) return "";
  const dateOnly = String(value).split("T")[0];
  return DATE_ONLY.test(dateOnly) ? dateOnly : "";
};

// The plant pours Monday through Saturday. Creede's Excel template only has
// weekday columns, which is why this used to skip the whole weekend — but he
// confirmed by email on 2026-07-21, asked directly whether Saturday pours ever
// happen: "Yes." Sunday is still not a pour day.
const SUNDAY = 0;

// Days the plant has been closed — weather, cleaning, a holiday. Passed in
// rather than read from the store so these helpers stay pure and testable; the
// store hands its own set to every call.
export type ClosedDays = ReadonlySet<string>;
const NO_CLOSURES: ClosedDays = new Set<string>();

export const isPourDay = (
  dateOnly: string,
  closed: ClosedDays = NO_CLOSURES
): boolean => {
  const [y, m, d] = dateOnly.split("-").map(Number);
  if (!y || !m || !d) return false;
  if (closed.has(dateOnly)) return false;
  return new Date(y, m - 1, d).getDay() !== SUNDAY;
};

const stampDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

// Next pour day strictly after a yyyy-MM-dd date, so shifting a Friday cell
// lands on Saturday and shifting a Saturday cell lands on Monday.
//
// Exported because the day-sheet printer needs the same walk. It previously
// carried a verbatim copy of this function, and a third near-identical walker
// existed alongside it — three implementations of "what is the next pour day"
// is two too many for a rule the client can change.
export const nextPourDay = (
  dateOnly: string,
  closed: ClosedDays = NO_CLOSURES,
  skipSaturdays = false
): string => {
  const [y, m, d] = dateOnly.split("-").map(Number);
  if (!y || !m || !d) return dateOnly;
  const next = new Date(y, m - 1, d + 1);
  // Bounded: a run of closed days should never spin. Two weeks of consecutive
  // closures is already beyond anything Creede described.
  let guard = 0;
  while (
    (next.getDay() === SUNDAY ||
      (skipSaturdays && next.getDay() === SATURDAY) ||
      closed.has(stampDate(next))) &&
    guard < 30
  ) {
    next.setDate(next.getDate() + 1);
    guard += 1;
  }
  return stampDate(next);
};

// Previous pour day strictly before a yyyy-MM-dd date. Needed to answer "does
// this form change mark from the day before?" — the highlight the plant crew
// reads to know which forms break down differently in the morning.
export const previousPourDay = (
  dateOnly: string,
  closed: ClosedDays = NO_CLOSURES,
  skipSaturdays = false
): string => {
  const [y, m, d] = dateOnly.split("-").map(Number);
  if (!y || !m || !d) return dateOnly;
  const prev = new Date(y, m - 1, d - 1);
  let guard = 0;
  while (
    (prev.getDay() === SUNDAY ||
      (skipSaturdays && prev.getDay() === SATURDAY) ||
      closed.has(stampDate(prev))) &&
    guard < 30
  ) {
    prev.setDate(prev.getDate() - 1);
    guard += 1;
  }
  return stampDate(prev);
};

// N consecutive pour days starting AT `startDateOnly`, which is always
// included: if Creede drops a mark on a Saturday cell he means that Saturday.
// Only the days AFTER the start skip Saturday when `skipSaturdays` is set —
// "operate under the assumption that we don't have to pour on Saturday", with
// the override for the weeks they do. Sunday is never a pour day either way.
//
// NOTE this is deliberately NOT a change to `isPourDay`: Saturday remains a
// real pour day, a grid column, and manually schedulable. Only auto-population
// skips it. (Last cycle established that; do not re-break it.)
const SATURDAY = 6;

// How many extra pour days a run-out may walk to step over slots that are
// already taken. Six weeks of Monday-to-Friday pours.
const RUN_OUT_SKIP_ALLOWANCE = 30;

export const pourDayRun = (
  startDateOnly: string,
  count: number,
  skipSaturdays: boolean,
  closed: ClosedDays = NO_CLOSURES
): string[] => {
  const [y, m, d] = startDateOnly.split("-").map(Number);
  if (!y || !m || !d || count <= 0) return [];

  const days: string[] = [];
  const cursor = new Date(y, m - 1, d);
  const stamp = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  const isRunDay = (date: Date): boolean => {
    const day = date.getDay();
    if (day === SUNDAY) return false;
    if (closed.has(stamp(date))) return false;
    return !(skipSaturdays && day === SATURDAY);
  };

  // Advance a start that is not a pour day at all. The grid has no Sunday
  // column so this should be unreachable from a drop, but a Sunday assignment
  // would be created and then filtered out of every cell — an invisible row
  // silently consuming the mark's quantity.
  // Advance off a Sunday OR a closed day: dropping onto a day the plant is shut
  // should start the run on the next day it actually pours.
  let startGuard = 0;
  while (
    (cursor.getDay() === SUNDAY || closed.has(stamp(cursor))) &&
    startGuard < 30
  ) {
    cursor.setDate(cursor.getDate() + 1);
    startGuard += 1;
  }

  days.push(stamp(cursor));
  // Runaway backstop only. It must never truncate a real run: 300 pieces over
  // five pour days a week is ~420 calendar days, and Creede described exactly
  // that job ("300 pieces might get two forms and 150 days"). Sized from the
  // request so the cap cannot silently shorten the schedule.
  const maxCalendarDays = count * 2 + 14;
  let guard = 0;
  while (days.length < count && guard < maxCalendarDays) {
    guard += 1;
    cursor.setDate(cursor.getDate() + 1);
    if (!isRunDay(cursor)) continue;
    days.push(stamp(cursor));
  }
  return days;
};

// Platform vocabulary. Precast pours on Platforms 1-5; VDT pours on
// Lines 1-3 (three lines per Creede's daily schedule template — the old
// two-line list was incomplete). The `platform` field stores the raw value
// ("1".."5" or "Line 1".."Line 3").
/**
 * How long a re-read is considered fresh enough to skip (item 18).
 *
 * `beforeEnter` fires on every navigation, and these stores pull several
 * thousand rows between them; the API allows 100 requests a minute. Fifteen
 * seconds stops a burst from tab-hopping while still feeling live -- and the
 * header's refresh button bypasses it entirely.
 */
export const MM_REFRESH_MIN_INTERVAL_MS = 15_000;

export const PRECAST_PLATFORMS = ["1", "2", "3", "4", "5"] as const;
export const VDT_LINES = ["Line 1", "Line 2", "Line 3"] as const;

export interface ScheduleRow {
  platform: string;
  label: string;
  productType: MarkProductType;
}

// Row order for the casting-schedule grid: precast platforms first, then
// the VDT lines — everything the plant pours, on one screen.
export const SCHEDULE_ROWS: ScheduleRow[] = [
  ...PRECAST_PLATFORMS.map((p) => ({
    platform: p,
    label: `Platform ${p}`,
    productType: "precast" as MarkProductType,
  })),
  ...VDT_LINES.map((line) => ({
    platform: line,
    label: line,
    productType: "vdt" as MarkProductType,
  })),
];

// Weight per cubic foot depends on product type: VDT is lighter than Precast.
export const lbsPerCuFt = (productType: MarkProductType): number =>
  productType === "vdt" ? 125 : 150;

export interface MMForm {
  id: string;
  projectId: string;
  formNumber: string;
  /**
   * Which phase's F1 this is (item 13). Form numbers restart each phase and the
   * new number is new steel, so without this two phases' F1 are one record —
   * the same isDouble flag, hours and status. Undefined = the job's only phase,
   * which is every form written before 2026-08-31.
   */
  phase?: string;
  baseFormId?: string;
  // Optional since 2026-08-06: Creede always has plant-manager hours when
  // creating a form but not always estimator hours, and gets the number later.
  // The API column is nullable as of the same day; `undefined` means "not
  // estimated yet", which is a different fact from an estimate of zero.
  estimatorHours?: number;
  plantManagerHours: number;
  actualHours?: number;
  status: FormStatus;
  builder?: string;
  // This form pours two pieces at once — a property of the steel, not of a
  // mark: "forms would be marked as a double, not marks" (Creede, 2026-08-19).
  isDouble: boolean;
  givenToMoldShopDate?: string;
  completedDate?: string;
}

export interface MMTicketGroup {
  id: string;
  projectId: string;
  name: string;
  /**
   * Optional since 2026-09-15: the ticket date lives on the MARK now, read off
   * the ticket. Groups created before that keep theirs and still show it.
   */
  ticketDate?: string;
  formNumbers: string[];
  /**
   * Which phase of the job these tickets belong to (item 13). Groups sharing a
   * phase name are one phase. Undefined = the job's only phase.
   */
  phase?: string;
  /**
   * Which concrete colour this group pours (2026-09-15). Undefined = the job's
   * colour, which is every single-colour job: "I shouldn't have to worry about
   * going through and selecting the color."
   *
   * Groups get split by colour even when they share forms -- Creede: "maybe
   * they share forms, but break them up by colors" -- so this belongs to the
   * group, not the form and not the mark.
   */
  color?: string;
  sortOrder: number;
}

/**
 * A running note at one of three scopes (item 15).
 *
 * Creede keeps these in his spreadsheet today at project, form-group and mark
 * level, and asked for ONE entry action with a scope selector rather than three
 * note systems: "this note we're going to categorize under, it's a project-wide
 * note; this one is specific to WEO2."
 */
export interface MMNote {
  id: string;
  scope: "project" | "ticketGroup" | "mark";
  projectId?: string;
  ticketGroupId?: string;
  productionItemId?: string;
  body: string;
  author?: string;
  /** `recast` carries pieces + occurredOn; `note` is plain prose. */
  kind: "note" | "recast";
  pieces?: number;
  occurredOn?: string;
  createdAt?: string;
}

export interface MMProductionItem {
  id: string;
  projectId: string;
  ticketGroupId: string;
  formId?: string;
  markNumber: string;
  productType: MarkProductType;
  quantity: number;
  cubicFeetPerPiece: number;
  totalCubicFeet: number;
  lbsPerPiece: number;
  platform: string;
  qtyPerForm: number;
  isDouble: boolean;
  piecesProduced: number;
  piecesWashed?: number;
  piecesExtra?: number;
  /**
   * Recast / QC-failure history as dated lines (item 14). Creede records the
   * date, the piece count and the reason, and the entries ACCUMULATE — writes
   * append via `recordRecast`, never overwrite.
   */
  notes?: string;
  /**
   * What the PDF reader settled on for this row, 0..1, AFTER the server-side
   * rules (the printed-total cross-check, page confidence) rather than the
   * model's raw self-report. Below REVIEW_CONFIDENCE_THRESHOLD the staging grid
   * flags it. Undefined on anything typed or pasted, which correctly means
   * "nothing was unsure about this".
   */
  reviewConfidence?: number;
  /** Which page(s) of the packet it came from, e.g. "4-6", so he can find it. */
  sourcePages?: string;
  /** Stamped by answering "is this a revised ticket?", never inferred. */
  revisionDate?: string;
  piecesDelivered: number;
  percentProduced: number;
  percentDelivered: number;
  ticketDate?: string;
  issueDate?: string;
  formCompletedDate?: string;
  productionCompletedDate?: string;
  // Casting-schedule slot: which day this mark pours (yyyy-MM-dd; the
  // platform half of the slot is the existing `platform` field).
  scheduledPourDate?: string;
  // Piece length in inches — drives in-cell ordering (precast biggest
  // first, VDT smallest first).
  lengthInches?: number;
}

// One placement of a mark: N pieces of it, in one form, on one platform, on
// one day. This is the entity the casting schedule is really made of — Creede
// schedules FORM-first ("I schedule by form and pick a mark to go in the
// form"), so a mark's 8 pieces occupy 8 cells, which the scalar
// platform/scheduledPourDate/formId fields on the mark could never express.
//
// `pourDate` is client-supplied; the server does not stamp it. Unscheduling
// REMOVES the row rather than blanking it — there is no "assignment with no
// day". The server keeps `mark.piecesProduced` in sync with the sum of these,
// so the daily report and the completion stamp keep working.
// A day the plant does not pour: weather, cleaning, a holiday marked weeks out.
export interface MMScheduleClosure {
  id: string;
  closureDate: string;
  reason?: string;
  platform?: string;
}

export type DoubleSide = "A" | "B";

export interface MMScheduleAssignment {
  id: string;
  productionItemId: string;
  formId?: string;
  platform?: string;
  // Which half of a double form: "A" or "B". undefined for an ordinary single
  // pour, which is every placement made before doubles existed.
  side?: DoubleSide;
  pourDate: string;
  quantity: number;
  piecesProduced: number;
  /**
   * The order it was put on its day (09-30 item 2) -- appended on every create
   * and move. Undefined on rows from before the column, which fall back to
   * createdAt.
   */
  sequence?: number;
  createdAt?: string;
}

// What a schedule cell actually contains: an assignment plus the mark it
// places. Both halves are needed to render a chip — the day's quantity comes
// from the assignment, the mark number and size from the item.
export interface MMScheduledPlacement {
  assignment: MMScheduleAssignment;
  item: MMProductionItem;
}

// A mark with pieces still to place, and how many are left.
export interface MMUnscheduledMark {
  item: MMProductionItem;
  remaining: number;
}

// An assignment row exactly as the API hands it back: DATE columns arrive as
// full ISO datetimes and tedious can return numerics as strings, so nothing is
// trusted until it goes through the mapper below.
interface RawScheduleAssignment {
  side?: string | null;
  assignmentId: string;
  productionItemId: string;
  formId?: string | null;
  platform?: string | null;
  pourDate?: string | null;
  quantity?: number | string | null;
  piecesProduced?: number | string | null;
  sequence?: number | string | null;
  createdAt?: string | null;
}

/**
 * Pieces cast that are good: produced minus those recast for QC. The failed
 * piece WAS cast, and stays counted on the day it was poured; it just is not a
 * piece of the mark. Creede, 2026-09-30: "bump a piece out of this produced
 * maybe into extra."
 */
/**
 * A 250 cu ft QC test block made for one mix on one day (2026-10-16 item 26).
 * Creede: "build in a spot for me to check a test block was made to restart
 * that running total."
 */
export interface MMTestBlock {
  id: string;
  mixNumber: string;
  madeOn: string;
}

/** Cu.ft of VDT a mix has poured toward its next test block. */
export interface MMTestBlockTally {
  mixNumber: string;
  /** The last block before the day asked about; null = none recorded. */
  lastBlockOn: string | null;
  /** Produced on the days after that block and before the day asked about. */
  beforeCuFt: number;
  /** That day: what was produced, and what is scheduled. */
  todayProducedCuFt: number;
  todayScheduledCuFt: number;
  /** A block was recorded ON that day. */
  blockMadeToday: boolean;
}

export const TEST_BLOCK_CU_FT = 250;

// A test-block row as the API hands it back.
interface RawTestBlock {
  testBlockId: string;
  mixNumber?: string | null;
  madeOn?: string | null;
}

export const goodPiecesOf = (item: { piecesProduced: number; piecesExtra?: number }): number =>
  Math.max(0, item.piecesProduced - (item.piecesExtra ?? 0));

/** Good pieces made beyond the mark's quantity -- a VDT overage (2026-10-07). */
export const overPiecesOf = (item: {
  piecesProduced: number;
  piecesExtra?: number;
  quantity: number;
}): number => Math.max(0, goodPiecesOf(item) - item.quantity);

/**
 * `count` form numbers running on from `first`, keeping its prefix and zero
 * padding: F21 x3 -> F21, F22, F23; D09 x2 -> D09, D10. Undefined when `first`
 * has no trailing number to count from.
 */
export const sequentialFormNumbers = (first: string, count: number): string[] | undefined => {
  const trimmed = first.trim();
  const match = trimmed.match(/^(.*?)(\d+)$/);
  if (!match) return count <= 1 && trimmed ? [trimmed] : undefined;
  const [, prefix, digits] = match;
  const start = Number(digits);
  return Array.from({ length: Math.max(1, Math.floor(count)) }, (_, i) =>
    `${prefix}${String(start + i).padStart(digits.length, "0")}`
  );
};

// Local calendar date, yyyy-MM-dd -- "today" as the plant means it.
const todayDateOnly = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// Drag order: sequence, then when it was first scheduled, then id. Rows from
// before the sequence column carry none and sort among themselves by createdAt
// (datetime resolves to ~3ms, so the id settles a tie), ahead of anything
// placed since.
const compareDragOrder = (a: MMScheduleAssignment, b: MMScheduleAssignment): number =>
  (a.sequence ?? 0) - (b.sequence ?? 0) ||
  (a.createdAt ?? "").localeCompare(b.createdAt ?? "") ||
  a.id.localeCompare(b.id);

const toAssignment = (row: RawScheduleAssignment): MMScheduleAssignment => ({
  id: row.assignmentId,
  productionItemId: row.productionItemId,
  formId: row.formId || undefined,
  platform: row.platform || undefined,
  // Same normalisation as every other date in this tenant: the API returns
  // full ISO datetimes but validates writes as format:'date', so a
  // read-modify-write of a raw value fails on the way back in.
  pourDate: toDateOnly(row.pourDate),
  quantity: Number(row.quantity) || 0,
  piecesProduced: Number(row.piecesProduced) || 0,
  side: row.side === "A" || row.side === "B" ? row.side : undefined,
  sequence: row.sequence == null ? undefined : Number(row.sequence),
  createdAt: row.createdAt ?? undefined,
});

/**
 * The rows one schedule screen shows. VDT and precast are scheduled on separate
 * screens (2026-09-15): Sherman, on the call -- "I think it'll be a separate
 * screen too" -- and Creede had no objection, because the two are packed nothing
 * alike. He still wants to SEE the whole plant at once, which the printed day
 * sheet already does and a read-only plant view will do later: "not so much
 * dedicated for scheduling, but more for viewing."
 */
export const scheduleRowsFor = (productType: MarkProductType): ScheduleRow[] =>
  SCHEDULE_ROWS.filter((row) => row.productType === productType);

const SCHEDULE_PLATFORMS = new Set(SCHEDULE_ROWS.map((r) => r.platform));

/** Which product type a platform belongs to, for guarding a drop. */
export const productTypeOfPlatform = (
  platform: string
): MarkProductType | undefined =>
  SCHEDULE_ROWS.find((row) => row.platform === platform)?.productType;

// What a mark's platform reads before the schedule has told it otherwise.
// Creede types this himself at mark creation, because the platform is not known
// until the pallet is placed.
export const UNKNOWN_PLATFORM = "unknown";

// A mark occupies a schedule slot only when it sits on a platform the grid
// renders a row for AND on a day the grid renders a column for; anything else
// ("unknown", blank, a retired platform name, a Sunday date from older data)
// has no cell to appear in, so it counts as not-yet-placed and stays in the
// Unscheduled panel rather than disappearing from both views.
export const isPlacedOnSchedule = (item: MMProductionItem): boolean =>
  !!item.scheduledPourDate &&
  SCHEDULE_PLATFORMS.has(item.platform) &&
  isPourDay(item.scheduledPourDate);

// Shared empty results for the map-backed lookups below. Those lookups hand
// back the cached bucket itself, so no caller may mutate what it receives.
const NO_FORMS: MMForm[] = [];
const NO_ITEMS: MMProductionItem[] = [];
const NO_PLACEMENTS: MMScheduledPlacement[] = [];

// ============================================================================
// STORE
// ============================================================================

class MMProductionStore {
  forms: MMForm[] = [];
  productionItems: MMProductionItem[] = [];
  notes: MMNote[] = [];
  // 250 cu ft QC test blocks, per mix number (2026-10-16 item 26).
  testBlocks: MMTestBlock[] = [];
  scheduleAssignments: MMScheduleAssignment[] = [];
  scheduleClosures: MMScheduleClosure[] = [];
  ticketGroups: MMTicketGroup[] = [];
  expandedGroupIds: string[] = [];
  selectedProjectId: string | null = null;
  // Currently unread — `selectForm` has no callers. Two getters used to filter
  // marks by it and were REMOVED (2026-08-06): they compared `item.formId`,
  // which holds the human form NUMBER, against this field, which holds a form
  // uuid, so they could never match. The correct form -> marks lookup is
  // `getMarksByFormNumber` / `marksByFormNumberMap`, keyed `projectId|formNumber`
  // because form numbers repeat across jobs.
  selectedFormId: string | null = null;
  // Which phase of the selected job the Tickets view is showing. null = all of
  // them, which is every job that has no phases (item 13).
  selectedPhase: string | null = null;
  // Which tab the Production page is showing: tickets (groups + marks),
  // forms, or the plant-wide casting schedule grid.
  productionTab: "tickets" | "forms" | "anchors" | "schedule" | "vdt" | "plant" = "tickets";
  /** Lower-cased email -> "First Last", for notes stamped before 09-16. */
  authorNamesByEmail: Map<string, string> = new Map();
  searchTerm: string = "";
  isLoading: boolean = false;
  error: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    // The map-backed lookups are pure readers, not actions. Left to
    // makeAutoObservable they become autoActions, which wraps every one of
    // the per-row calls in an action batch and only keeps their reads tracked
    // while they run nested inside a live derivation. Excluded, they stay
    // plain functions whose reads always belong to the calling observer.
    makeAutoObservable(this, {
      rootStore: false,
      getFormCopies: false,
      getMarksByFormNumber: false,
      getScheduleCellPlacements: false,
    });
    this.rootStore = rootStore;

    // Auto-load when authenticated
    when(
      () => !!(this.rootStore.authStore as any)?.isSignedIn,
      () => this.loadData()
    );
  }

  // ========================================
  // SERVICE HELPERS
  // ========================================

  private get formsService() {
    return this.rootStore.app.service("mm-forms");
  }

  private get ticketGroupsService() {
    return this.rootStore.app.service("mm-ticket-groups");
  }

  private get productionItemsService() {
    return this.rootStore.app.service("mm-production-items");
  }

  // Deliberately NOT registered in src/serviceStores/miller-mize.stores.ts —
  // that whole file is dead configuration, never imported by
  // serviceStoresConfig.ts. Every MM store talks to Feathers directly, and
  // this follows the same pattern.
  private get scheduleClosuresService() {
    return this.rootStore.app.service("mm-schedule-closures") as any;
  }

  private get scheduleAssignmentsService() {
    return this.rootStore.app.service("mm-schedule-assignments");
  }

  private get notesService() {
    return this.rootStore.app.service("mm-notes") as any;
  }

  private get testBlocksService() {
    return this.rootStore.app.service("mm-test-blocks") as any;
  }

  private get usersService() {
    return this.rootStore.app.service("user") as any;
  }

  private get ticketExtractService() {
    return this.rootStore.app.service("mm-ticket-extract") as any;
  }


  // ========================================
  // REFRESH (item 18)
  // ========================================
  //
  // Creede, 2026-09-15: "I was just having some refresh issues when I update the
  // status of a load... if I went back and made it not delivered and went back
  // into the job, it wouldn't populate until I... it's just a slow refresh, I
  // guess. I would wait a couple minutes and it'd be fine."
  //
  // It was not slowness. Every mm-* service declares `events: []`, so the server
  // broadcasts nothing, the stores register no socket listeners, and `loadData`
  // ran EXACTLY ONCE per tab -- from the constructor, on sign-in. A change made
  // in one tab (or by Dutch, in his own session) never arrived in another at
  // all, and the couple of minutes was most likely him deciding to reload.
  //
  // The cheap half of the fix, and deliberately the cheap half: re-read on
  // entering the screen, plus the header's refresh button, which re-runs the
  // same route hook. Real-time events are the correct answer and a bigger
  // change -- every service becomes a broadcaster, and these stores patch their
  // own state optimistically, so incoming events would have to stop fighting
  // those local merges. That belongs in a cycle with its own testing.
  lastLoadedAt = 0;

  /**
   * Re-read everything this store owns.
   *
   * THROTTLED, because `beforeEnter` fires on every navigation and this pulls
   * several thousand rows: tab-hopping between Tickets and Loads would
   * otherwise fire a burst against an API that allows 100 requests a minute.
   * The refresh button passes `force`, so there is always a way to insist.
   */
  refresh = async ({ force = false }: { force?: boolean } = {}) => {
    if (this.isLoading) return;
    if (!force && Date.now() - this.lastLoadedAt < MM_REFRESH_MIN_INTERVAL_MS) {
      return;
    }
    await this.loadData();
  };

  // ========================================
  // DATA LOADING
  // ========================================

  loadData = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const [
        formsResult,
        groupsResult,
        itemsResult,
        assignmentsResult,
        closuresResult,
        notesResult,
        usersResult,
        testBlocksResult,
      ] = await Promise.all([
          this.formsService.find({ query: { $limit: 5000 } }),
          this.ticketGroupsService.find({ query: { $limit: 2000 } }),
          this.productionItemsService.find({ query: { $limit: 10000 } }),
          // active: true matters. A soft-deleted assignment would otherwise go
          // on consuming its mark's schedule quantity and inflating its
          // produced total — the server guards its own sums the same way.
          this.scheduleAssignmentsService.find({
            query: { $limit: 10000, active: true },
          }),
          // Closed days. Caught rather than thrown so a tenant whose API
          // predates this service still loads its schedule — the whole grid
          // going blank over a missing closures endpoint would be a far worse
          // failure than closures not showing.
          this.scheduleClosuresService
            .find({ query: { $limit: 2000, active: true } })
            .catch(() => ({ data: [] })),
          // Same tolerance as closures: a tenant whose API predates mm-notes
          // still loads its production screen. Notes not showing is a far
          // smaller failure than the whole tab going blank.
          this.notesService
            .find({ query: { $limit: 5000, active: true } })
            .catch(() => ({ data: [] })),
          // Who wrote what. Notes stamped before 2026-09-16 carry an email
          // login rather than a name, and Creede asked to see names: "instead
          // of our email address login, if you could just make it the name
          // associated with that user." Resolving at display time fixes the
          // notes he has already written without rewriting an audit stamp.
          //
          // Same tolerance as the two above: names not resolving is a smaller
          // failure than the tab going blank.
          this.usersService
            .find({ query: { $limit: 500 } })
            .catch(() => ({ data: [] })),
          // 10-16 item 26. Same tolerance: an API a deploy behind shows no
          // test blocks rather than no production screen.
          this.testBlocksService
            .find({ query: { $limit: 5000, active: true } })
            .catch(() => ({ data: [] })),
        ]);
      const testBlocksData: RawTestBlock[] = Array.isArray(testBlocksResult)
        ? testBlocksResult
        : (testBlocksResult as { data?: RawTestBlock[] })?.data || [];

      const formsData: any[] = Array.isArray(formsResult)
        ? formsResult
        : (formsResult as any).data || [];
      const groupsData: any[] = Array.isArray(groupsResult)
        ? groupsResult
        : (groupsResult as any).data || [];
      const itemsData: any[] = Array.isArray(itemsResult)
        ? itemsResult
        : (itemsResult as any).data || [];
      const assignmentsData: RawScheduleAssignment[] = Array.isArray(
        assignmentsResult
      )
        ? assignmentsResult
        : (assignmentsResult as { data?: RawScheduleAssignment[] }).data || [];
      const closuresData: any[] = Array.isArray(closuresResult)
        ? closuresResult
        : (closuresResult as any)?.data || [];
      const notesData: RawNote[] = Array.isArray(notesResult)
        ? notesResult
        : (notesResult as { data?: RawNote[] })?.data || [];

      const usersData: any[] = Array.isArray(usersResult)
        ? usersResult
        : (usersResult as { data?: any[] })?.data || [];
      const namesByEmail = new Map<string, string>();
      for (const user of usersData) {
        const email = String(user?.email ?? "").trim().toLowerCase();
        const name = [user?.firstName, user?.lastName]
          .filter((part) => !!part && String(part).trim() !== "")
          .join(" ")
          .trim();
        if (email && name) namesByEmail.set(email, name);
      }

      const forms: MMForm[] = formsData.map((f: any) => ({
        id: f.formId,
        projectId: f.projectId,
        formNumber: f.formNumber,
        phase: f.phase || undefined,
        baseFormId: f.baseFormId || undefined,
        estimatorHours:
          f.estimatorHours != null ? Number(f.estimatorHours) : undefined,
        plantManagerHours: Number(f.plantManagerHours) || 0,
        actualHours: f.actualHours != null ? Number(f.actualHours) : undefined,
        status: f.status || "pending",
        builder: f.builder || undefined,
        isDouble: !!f.isDouble,
        givenToMoldShopDate: toDateOnly(f.givenToMoldShopDate) || undefined,
        completedDate: toDateOnly(f.completedDate) || undefined,
      }));

      const ticketGroups: MMTicketGroup[] = groupsData.map((g: any) => ({
        id: g.ticketGroupId,
        projectId: g.projectId,
        name: g.name,
        ticketDate: toDateOnly(g.ticketDate) || undefined,
        formNumbers:
          typeof g.formNumbers === "string"
            ? JSON.parse(g.formNumbers)
            : g.formNumbers || [],
        phase: g.phase || undefined,
        color: g.color || undefined,
        sortOrder: Number(g.sortOrder) || 0,
      }));

      const productionItems: MMProductionItem[] = itemsData.map((p: any) => ({
        id: p.productionItemId,
        projectId: p.projectId,
        ticketGroupId: p.ticketGroupId,
        formId: p.formId || undefined,
        markNumber: p.markNumber || "",
        productType: p.productType || "precast",
        quantity: Number(p.quantity) || 0,
        cubicFeetPerPiece: Number(p.cubicFeetPerPiece) || 0,
        totalCubicFeet: Number(p.totalCubicFeet) || 0,
        lbsPerPiece: Number(p.lbsPerPiece) || 0,
        platform: p.platform || UNKNOWN_PLATFORM,
        qtyPerForm: Number(p.qtyPerForm) || 1,
        isDouble: !!p.isDouble,
        piecesProduced: Number(p.piecesProduced) || 0,
        piecesWashed: p.piecesWashed != null ? Number(p.piecesWashed) : undefined,
        piecesExtra: p.piecesExtra != null ? Number(p.piecesExtra) : undefined,
        notes: p.notes || undefined,
        reviewConfidence:
          p.reviewConfidence == null ? undefined : Number(p.reviewConfidence),
        sourcePages: p.sourcePages || undefined,
        revisionDate: p.revisionDate ? toDateOnly(p.revisionDate) : undefined,
        piecesDelivered: Number(p.piecesDelivered) || 0,
        percentProduced: Number(p.percentProduced) || 0,
        percentDelivered: Number(p.percentDelivered) || 0,
        ticketDate: toDateOnly(p.ticketDate) || undefined,
        issueDate: toDateOnly(p.issueDate) || undefined,
        formCompletedDate: toDateOnly(p.formCompletedDate) || undefined,
        productionCompletedDate:
          toDateOnly(p.productionCompletedDate) || undefined,
        scheduledPourDate: toDateOnly(p.scheduledPourDate) || undefined,
        lengthInches:
          p.lengthInches != null ? Number(p.lengthInches) : undefined,
      }));

      const scheduleAssignments: MMScheduleAssignment[] =
        assignmentsData.map(toAssignment);

      runInAction(() => {
        this.forms = forms;
        this.ticketGroups = ticketGroups;
        this.productionItems = productionItems;
        this.scheduleAssignments = scheduleAssignments;
        this.scheduleClosures = closuresData.map((c: any) => ({
          id: c.closureId ?? c.id,
          closureDate: toDateOnly(c.closureDate),
          reason: c.reason || undefined,
          platform: c.platform || undefined,
        }));
        this.notes = notesData.map(mapNote);
        this.testBlocks = testBlocksData.map((b) => ({
          id: b.testBlockId,
          mixNumber: String(b.mixNumber ?? "").trim(),
          madeOn: toDateOnly(b.madeOn),
        }));
        this.authorNamesByEmail = namesByEmail;
        this.isLoading = false;
        this.lastLoadedAt = Date.now();
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.lastLoadedAt = Date.now();
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to load production data";
      });
      console.error("Failed to load production data:", error);
    }
  };

  // ========================================
  // COMPUTED - FORMS
  // ========================================

  get formsByProject(): MMForm[] {
    if (!this.selectedProjectId) return [];
    return this.forms.filter((f) => f.projectId === this.selectedProjectId);
  }

  getFormById = (id: string): MMForm | undefined => {
    return this.forms.find((f) => f.id === id);
  };

  // Sorted numerically by form number — this feeds the form pickers, where the
  // raw API order read as arbitrary. Fresh array from filter(), so sorting in
  // place does not touch the observable.
  // Mark id -> its phase, via its ticket group. Computed rather than looked up
  // per call: the schedule guards below compare every assignment against every
  // other one, and a linear find inside those loops is quadratic.
  private get phaseByItemId(): Map<string, string> {
    const groupPhase = new Map(
      this.ticketGroups.map((group) => [group.id, phaseKey(group.phase)])
    );
    return new Map(
      this.productionItems.map((item) => [
        item.id,
        groupPhase.get(item.ticketGroupId ?? "") ?? "",
      ])
    );
  }

  // The phase a mark belongs to, or "" for a job with no phases.
  phaseOfItem = (itemId?: string): string =>
    (itemId && this.phaseByItemId.get(itemId)) || "";

  // The phase a placement belongs to, via the mark it places.
  private phaseOfAssignment = (assignment: {
    productionItemId: string;
  }): string => this.phaseOfItem(assignment.productionItemId);

  // The phase a form's STEEL belongs to, which is not always the phase of the
  // mark pouring in it. A job's unphased form serves every phase (formInPhase),
  // so it is one physical form -- and taking the phase from the MARK gave it a
  // different identity per phase, letting phase 1 and phase 2 double-book it on
  // one day with no warning. Creede, 2026-09-18: "two phases that share a form
  // can occasionally be poured on the same day." So they must see each other.
  //
  // Only when no record says otherwise: a phase that built its own steel under
  // that number keeps it (the most specific record wins, as isDoubleFormNumber
  // does), and a number with no record at all keeps the old per-phase reading.
  private steelPhase = (
    projectId: string | undefined,
    formId: string | null | undefined,
    phase: string
  ): string => {
    const key = formKey(formId);
    if (!projectId || !key || !phase) return phase;
    const records = this.forms.filter(
      (form) => form.projectId === projectId && formKey(form.formNumber) === key
    );
    const own = records.some((form) => phaseKey(form.phase) === phase);
    const shared = records.some((form) => phaseKey(form.phase) === "");
    return shared && !own ? "" : phase;
  };

  // The identity a mark's form has on the schedule: (steel phase, number). Two
  // placements share a form only when both match -- phase 2's own F1 is
  // different steel, and a job's shared F1 is the same steel in every phase.
  //
  // And the JOB (09-30 item 16). Forms belong to a job, so job A's F1 and job
  // B's F1 are different steel -- yet they used to block each other on the same
  // platform and day. Creede, 2026-09-29: "each mold would still be identified
  // by its own job information and mold number."
  formIdentityForItem = (itemId: string, formId?: string | null): string => {
    const steel = this.steelIdentityForItem(itemId, formId);
    if (!steel) return "";
    const item = this.productionItems.find((p) => p.id === itemId);
    return `${item?.projectId ?? ""}\u0001${steel}`;
  };

  // (steel phase, form number) without the job -- for callers that are asked
  // about a form by number and may not know whose it is.
  private steelIdentityForItem = (itemId: string, formId?: string | null): string => {
    const item = this.productionItems.find((p) => p.id === itemId);
    return formIdentity(
      formId,
      this.steelPhase(item?.projectId, formId, this.phaseOfItem(itemId))
    );
  };

  private assignmentFormIdentity = (assignment: {
    productionItemId: string;
    formId?: string | null;
  }): string => this.formIdentityForItem(assignment.productionItemId, assignment.formId);

  // The forms belonging to one phase of a job. Form numbers restart each
  // phase and the new number is new steel, so a phase-2 ticket group must not
  // be offered phase 1's forms -- picking one would silently point the group at
  // the wrong physical form.
  //
  // A form with NO phase is the exception -- see formInPhase. Creede, job 3938:
  // naming a phase on an existing job left the Edit Ticket Group dropdown with
  // no options at all, because the job's forms all predated the phase. Hiding a
  // job's own steel from it is the worse error of the two -- there is nothing
  // to pick instead -- and a form that DOES name a phase is still fenced to it.
  getFormsByPhase = (projectId: string, phase: string | null): MMForm[] => {
    const forms = this.getFormsByProject(projectId);
    if (phase == null) return forms;
    // Item 8 (2026-09-08) is the other half of this: a form's phase is now
    // settable on edit, so one that was written blank by accident can be
    // fenced to its phase afterwards rather than staying everyone's steel.
    return forms.filter((form) => formInPhase(form.phase, phase));
  };

  /**
   * Other ticket groups on this job, in the SAME phase, that already list this
   * form number. Item 10, Creede 2026-09-04: one form can serve ticket groups
   * in DIFFERENT phases, but that "never happens within one phase".
   *
   * A warning rather than a refusal, by Sherman's call (2026-09-10) and the
   * convention everywhere else in this app: it catches the typo that would
   * let the scheduler count one form's capacity twice, without fighting him on
   * the day he genuinely means it.
   *
   * Phases compared through phaseKey, so "pHASE 1" and "Phase 1" are the same
   * phase -- job 3938's lesson. A job with no phases has one phase, "", and
   * the rule holds there too.
   */
  groupsSharingFormInPhase = (
    projectId: string,
    formNumber: string,
    phase: string | null | undefined,
    excludeGroupId?: string
  ): MMTicketGroup[] => {
    const wantedForm = formKey(formNumber);
    const wantedPhase = phaseKey(phase);
    if (!wantedForm) return [];
    return this.ticketGroups.filter(
      (group) =>
        group.projectId === projectId &&
        group.id !== excludeGroupId &&
        phaseKey(group.phase) === wantedPhase &&
        group.formNumbers.some((number) => formKey(number) === wantedForm)
    );
  };

  /**
   * The phases a form actually serves, for display -- "Phase 1 and Phase 2".
   *
   * DERIVED from the ticket groups that list it, not stored: a form's phases
   * are simply the phases of the groups using it, and a stored list would be a
   * second source of truth that drifts the first time a group is edited.
   *
   *   - A form TAGGED to a phase is that phase's steel only.
   *   - An UNPHASED form is shared, and serves every phase whose groups list
   *     its number -- EXCEPT a phase that has built its own form with that
   *     number. There, the phase's own record answers for the number and the
   *     shared one is not consulted ("the most specific record wins", d1c8af9).
   *
   * Sorted in the header's own phase order, so the two agree.
   */
  phasesServedByForm = (form: MMForm): string[] => {
    const own = (form.phase ?? "").trim();
    if (own) return [own];

    const number = formKey(form.formNumber);
    const phasesWithOwnSteel = new Set(
      this.getFormsByProject(form.projectId)
        .filter((f) => formKey(f.formNumber) === number && (f.phase ?? "").trim())
        .map((f) => phaseKey(f.phase))
    );
    const served = new Set(
      this.ticketGroups
        .filter(
          (group) =>
            group.projectId === form.projectId &&
            (group.phase ?? "").trim() &&
            !phasesWithOwnSteel.has(phaseKey(group.phase)) &&
            group.formNumbers.some((n) => formKey(n) === number)
        )
        .map((group) => phaseKey(group.phase))
    );
    return this.phasesForProject(form.projectId).filter((label) =>
      served.has(phaseKey(label))
    );
  };

  getFormsByProject = (projectId: string): MMForm[] => {
    return this.forms
      .filter((f) => f.projectId === projectId)
      .sort((a, b) => markNumberOrder.compare(a.formNumber, b.formNumber));
  };

  get baseFormsByProject(): MMForm[] {
    if (!this.selectedProjectId) return [];
    let result = this.forms.filter(
      (f) => f.projectId === this.selectedProjectId && !f.baseFormId
    );

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      // Copies come from the bucketed map rather than a nested scan of every
      // form in the plant — this getter re-runs on each keystroke.
      result = result.filter(
        (f) =>
          f.formNumber.toLowerCase().includes(term) ||
          (this.formCopiesMap.get(f.id) || NO_FORMS).some((c) =>
            c.formNumber.toLowerCase().includes(term)
          )
      );
    }

    // Form order was raw API response order, which neither Creede nor Sherman
    // could account for. Numeric collation so F2 precedes F10. `result` is
    // always a fresh array from filter(), so sorting in place is safe.
    return result.sort((a, b) =>
      markNumberOrder.compare(a.formNumber, b.formNumber)
    );
  }

  // One pass over the forms list into a baseFormId -> copies map. Every
  // Forms-tab row asks for its own copies, so the per-row full scan this
  // replaces cost O(rows x forms) per render of the table.
  get formCopiesMap(): Map<string, MMForm[]> {
    const map = new Map<string, MMForm[]>();
    for (const form of this.forms) {
      if (!form.baseFormId) continue;
      const copies = map.get(form.baseFormId);
      if (copies) {
        copies.push(form);
      } else {
        map.set(form.baseFormId, [form]);
      }
    }
    for (const copies of map.values()) {
      copies.sort((a, b) =>
        markNumberOrder.compare(a.formNumber, b.formNumber)
      );
    }
    return map;
  }

  getFormCopies = (baseFormId: string): MMForm[] => {
    return this.formCopiesMap.get(baseFormId) || NO_FORMS;
  };

  hasFormCopies = (formId: string): boolean => {
    return this.forms.some((f) => f.baseFormId === formId);
  };

  get formStats() {
    const forms = this.formsByProject;
    // The estimator's own figures live on the project (10-27 item 1): "He will
    // estimate a total number of forms and a total number of hours." Until
    // they are entered, hours fall back to the per-form estimator hours.
    const project = this.selectedProjectId
      ? this.rootStore.mmProjectStore?.getProjectById(this.selectedProjectId)
      : undefined;
    const estimatorHoursFromForms = project?.estimatorFormHours == null;
    const totalEstimatorHours = estimatorHoursFromForms
      ? forms.reduce((sum, f) => sum + (f.estimatorHours || 0), 0)
      : (project!.estimatorFormHours as number);
    const totalPlantManagerHours = forms.reduce(
      (sum, f) => sum + f.plantManagerHours,
      0
    );
    const totalActualHours = forms.reduce(
      (sum, f) => sum + (f.actualHours || 0),
      0
    );

    // Forms, three ways (10-27): the estimator's count, the plant manager's
    // (the forms entered here -- "the number of forms is how many forms the
    // plant manager estimated"), and the actual (those completed; D-F).
    const completedForms = forms.filter((f) => f.status === "completed").length;

    return {
      totalForms: forms.length,
      estimatorForms: project?.estimatorForms ?? null,
      estimatorHoursFromForms,
      completedForms,
      pendingForms: forms.filter((f) => f.status === "pending").length,
      inProgressForms: forms.filter((f) => f.status === "in_progress").length,
      totalEstimatorHours,
      totalPlantManagerHours,
      totalActualHours,
      hoursVariance: totalActualHours - totalEstimatorHours,
      // Plant manager vs the estimate, and actual vs the plant manager.
      plantManagerVariance: totalPlantManagerHours - totalEstimatorHours,
      actualVsPlantManager: totalActualHours - totalPlantManagerHours,
    };
  }

  // ========================================
  // COMPUTED - TICKET GROUPS
  // ========================================

  get ticketGroupsByProject(): MMTicketGroup[] {
    if (!this.selectedProjectId) return [];
    let result = this.ticketGroups.filter(
      (g) => g.projectId === this.selectedProjectId && this.groupInSelectedPhase(g)
    );

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(
        (g) =>
          g.name.toLowerCase().includes(term) ||
          g.formNumbers.some((f) => f.toLowerCase().includes(term)) ||
          this.productionItems.some(
            (p) =>
              p.ticketGroupId === g.id &&
              p.markNumber.toLowerCase().includes(term)
          )
      );
    }

    return result.sort(compareTicketGroups);
  }

  getTicketGroupById = (id: string): MMTicketGroup | undefined => {
    return this.ticketGroups.find((g) => g.id === id);
  };

  getProductionItemsByGroup = (groupId: string): MMProductionItem[] => {
    const items = this.productionItems
      .filter((p) => p.ticketGroupId === groupId)
      .sort((a, b) =>
        markNumberOrder.compare(a.markNumber, b.markNumber)
      );
    if (!this.searchTerm) return items;

    // When the group itself matches (name or form number), show all its
    // marks; otherwise narrow to the marks that match the search term.
    const term = this.searchTerm.toLowerCase();
    const group = this.ticketGroups.find((g) => g.id === groupId);
    const groupMatches =
      !!group &&
      (group.name.toLowerCase().includes(term) ||
        group.formNumbers.some((f) => f.toLowerCase().includes(term)));
    if (groupMatches) return items;
    return items.filter((p) => p.markNumber.toLowerCase().includes(term));
  };

  getGroupStats = (groupId: string) => {
    // Stats always reflect the full group, not the search-filtered view.
    const items = this.productionItems.filter(
      (p) => p.ticketGroupId === groupId
    );
    const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
    // Good pieces: a piece recast for QC is not produced (09-30 item 9).
    const totalProduced = items.reduce((sum, i) => sum + goodPiecesOf(i), 0);
    const totalDelivered = items.reduce((sum, i) => sum + i.piecesDelivered, 0);

    return {
      totalMarks: items.length,
      totalQuantity,
      totalProduced,
      totalDelivered,
      percentProduced:
        totalQuantity > 0
          ? Math.round((totalProduced / totalQuantity) * 100)
          : 0,
      percentDelivered:
        totalQuantity > 0
          ? Math.round((totalDelivered / totalQuantity) * 100)
          : 0,
    };
  };

  get ungroupedItems(): MMProductionItem[] {
    if (!this.selectedProjectId) return [];
    const groupIds = new Set(this.ticketGroups.map((g) => g.id));
    let result = this.productionItems.filter(
      (p) =>
        p.projectId === this.selectedProjectId &&
        !groupIds.has(p.ticketGroupId)
    );

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter((p) =>
        p.markNumber.toLowerCase().includes(term)
      );
    }

    // Rows the reader was unsure about come FIRST, then mark order. On a
    // sixty-page import the flagged handful is the whole point of staging, and
    // finding them by scrolling a few hundred marks in mark order is the work
    // this was meant to remove. Within each band the order is unchanged, so a
    // job with nothing flagged looks exactly as it did.
    return result.sort((a, b) => {
      const checkA =
        typeof a.reviewConfidence === "number" &&
        a.reviewConfidence < REVIEW_CONFIDENCE_THRESHOLD;
      const checkB =
        typeof b.reviewConfidence === "number" &&
        b.reviewConfidence < REVIEW_CONFIDENCE_THRESHOLD;
      if (checkA !== checkB) return checkA ? -1 : 1;
      return markNumberOrder.compare(a.markNumber, b.markNumber);
    });
  }

  // ========================================
  // COMPUTED - PRODUCTION ITEMS
  // ========================================

  get productionItemsByProject(): MMProductionItem[] {
    if (!this.selectedProjectId) return [];
    return this.productionItems.filter(
      (p) => p.projectId === this.selectedProjectId
    );
  }

  // The job's ACTUAL cubic footage, summed off the production tickets. This is
  // the figure Creede compares against the estimate on the project record:
  // "this job is actually 200 cubic feet and not 173 cubic feet." Zero until
  // tickets are entered, which is why the estimate has to keep governing until
  // he says the ticket set is complete.
  ticketedCubicFeetByProject = (projectId: string): number => {
    const total = this.productionItems
      .filter((p) => p.projectId === projectId)
      .reduce((sum, item) => sum + item.totalCubicFeet, 0);
    return Math.round(total * 100) / 100;
  };

  getProductionItemById = (id: string): MMProductionItem | undefined => {
    return this.productionItems.find((p) => p.id === id);
  };

  // Sorted by form group, then mark number within the group — Creede: WA01 and
  // WA02 share a form group but did not land next to each other, because this
  // returned raw API order. Marks with no form sort last, matching the
  // unscheduled panel's grouping. `formId` here is the human form NUMBER, so
  // it needs numeric collation ("F2" before "F10").
  // A job is finished when every piece on it has been delivered. There is no
  // terminal status on the project record to read instead (detailingStatus ends
  // at `plant`, which means IN production), so it is derived here, once, for
  // the two places that hand a schedule colour out and take it back.
  //
  // A job with no marks is unstarted, not finished.
  isProjectFullyDelivered = (projectId: string): boolean => {
    const marks = this.productionItems.filter((p) => p.projectId === projectId);
    return (
      marks.length > 0 &&
      marks.every((mark) => (mark.piecesDelivered ?? 0) >= mark.quantity)
    );
  };

  getProductionItemsByProject = (projectId: string): MMProductionItem[] => {
    return this.productionItems
      .filter((p) => p.projectId === projectId)
      .slice()
      .sort((a, b) => {
        // Normalise FIRST. "no form" has two representations that both reach
        // this list: never-assigned marks hydrate as `undefined`, while
        // `assignMarkToForm(id, null)` — dropping a mark back into the
        // Unassigned pool — writes `""`. Comparing the raw values means
        // `"" !== undefined` enters the branch from BOTH directions and
        // returns 1 either way, so the comparator is not antisymmetric, the
        // mark-number tiebreak is never reached, and the whole "no form"
        // group comes out in raw API order.
        const formA = a.formId || "";
        const formB = b.formId || "";
        if (formA !== formB) {
          if (!formA) return 1;
          if (!formB) return -1;
          const byForm = markNumberOrder.compare(formA, formB);
          if (byForm !== 0) return byForm;
        }
        return markNumberOrder.compare(a.markNumber, b.markNumber);
      });
  };

  // One pass over ALL production items into a `${projectId}|${formNumber}`
  // -> marks map, each bucket sorted by mark number. `formId` holds the human
  // form NUMBER (see MarkFormDialog's "Produced with Form" select) and form
  // numbers repeat across jobs, so the project has to be part of the key or
  // one job's marks leak into another's. Keying on it rather than filtering
  // against selectedProjectId also keeps the map independent of the selection:
  // switching jobs is a different lookup, not a rebuild of every bucket.
  get marksByFormNumberMap(): Map<string, MMProductionItem[]> {
    const map = new Map<string, MMProductionItem[]>();
    for (const item of this.productionItems) {
      if (!item.formId) continue;
      const key = `${item.projectId}|${item.formId}`;
      const marks = map.get(key);
      if (marks) {
        marks.push(item);
      } else {
        map.set(key, [item]);
      }
    }

    for (const marks of map.values()) {
      marks.sort((a, b) =>
        markNumberOrder.compare(a.markNumber, b.markNumber)
      );
    }
    return map;
  }

  // Marks assigned to a given form number, scoped to the selected project.
  // Feeds the mark-chip row on each Forms-tab row.
  getMarksByFormNumber = (formNumber: string): MMProductionItem[] => {
    if (!this.selectedProjectId) return NO_ITEMS;
    return (
      this.marksByFormNumberMap.get(
        `${this.selectedProjectId}|${formNumber}`
      ) || NO_ITEMS
    );
  };

  get productionStats() {
    // Scoped to the selected phase — the totals are the whole reason phases
    // exist. Unscoped when no phase is selected, which is every ordinary job.
    const items = this.productionItemsByProject.filter((item) =>
      this.itemInSelectedPhase(item)
    );
    const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
    // Good pieces: a piece recast for QC is not produced (09-30 item 9).
    const totalProduced = items.reduce((sum, i) => sum + goodPiecesOf(i), 0);
    const totalDelivered = items.reduce(
      (sum, i) => sum + i.piecesDelivered,
      0
    );
    const totalCuFt = items.reduce((sum, i) => sum + i.totalCubicFeet, 0);
    const producedCuFt = items.reduce(
      (sum, i) => sum + goodPiecesOf(i) * i.cubicFeetPerPiece,
      0
    );
    const deliveredCuFt = items.reduce(
      (sum, i) => sum + i.piecesDelivered * i.cubicFeetPerPiece,
      0
    );

    return {
      totalMarks: items.length,
      totalQuantity,
      totalProduced,
      totalDelivered,
      totalCuFt: Math.round(totalCuFt * 100) / 100,
      producedCuFt: Math.round(producedCuFt * 100) / 100,
      deliveredCuFt: Math.round(deliveredCuFt * 100) / 100,
      percentProduced:
        totalQuantity > 0
          ? Math.round((totalProduced / totalQuantity) * 100)
          : 0,
      percentDelivered:
        totalQuantity > 0
          ? Math.round((totalDelivered / totalQuantity) * 100)
          : 0,
    };
  }

  // ========================================
  // COMPUTED - CASTING SCHEDULE
  // ========================================

  // One pass over ALL production items (the whole plant pours together,
  // regardless of which job is selected) into a
  // `${platform}|${yyyy-MM-dd}` -> marks map, each cell pre-sorted:
  // precast platforms biggest-first, VDT lines smallest-first, by
  // lengthInches with cubicFeetPerPiece as the fallback size until the
  // length field is populated; stable tiebreak on mark number.
  // Marks indexed by id, so the cell map can resolve an assignment's mark
  // without a linear scan per row.
  private get itemsById(): Map<string, MMProductionItem> {
    const map = new Map<string, MMProductionItem>();
    for (const item of this.productionItems) map.set(item.id, item);
    return map;
  }

  // Live assignments grouped by the mark they place. Drives the remainder
  // ("6 of 8 left") the unscheduled panel shows.
  get assignmentsByItemId(): Map<string, MMScheduleAssignment[]> {
    const map = new Map<string, MMScheduleAssignment[]>();
    for (const assignment of this.scheduleAssignments) {
      const bucket = map.get(assignment.productionItemId);
      if (bucket) bucket.push(assignment);
      else map.set(assignment.productionItemId, [assignment]);
    }
    return map;
  }

  // Once a mark has placements, `piecesProduced` is DERIVED server-side from
  // their sum — so writing it directly on the mark is a silent no-op that the
  // next assignment write reverts. Views use this to send the user to the
  // schedule instead of letting them type into a field that will not stick.
  hasScheduleAssignments = (itemId: string): boolean =>
    (this.assignmentsByItemId.get(itemId) || []).length > 0;

  // Pieces of a mark already placed somewhere on the schedule.
  /**
   * The forms a mark is scheduled in, from the schedule itself -- read-only.
   * Creede, 2026-09-30: "as it's getting scheduled, you're gonna pick up what
   * it's getting poured in." Derived, never written to mark.formId: that field
   * is a FORCED form, and resolveFormForDrop sends every later drop to it.
   */
  scheduledFormsForItem = (itemId: string): string[] =>
    [...new Set(
      (this.assignmentsByItemId.get(itemId) || [])
        .map((assignment) => (assignment.formId ?? "").trim())
        .filter(Boolean)
    )].sort(compareNaturally);

  scheduledQuantityForItem = (itemId: string): number =>
    (this.assignmentsByItemId.get(itemId) || []).reduce(
      (sum, assignment) => sum + assignment.quantity,
      0
    );

  // Pieces of a mark still to place. The server enforces the same ceiling and
  // rejects an over-schedule with a message naming the remainder.
  // Pieces still to place: the mark's quantity, PLUS any recast for QC -- each
  // failed piece has to be poured again (09-30 item 9). Creede: "that should
  // bump a piece out of this produced maybe into extra and allow me to schedule
  // an additional piece." The API caps scheduling the same way.
  remainingQuantityForItem = (itemId: string): number => {
    const item = this.itemsById.get(itemId);
    if (!item) return 0;
    return Math.max(
      0,
      item.quantity + (item.piecesExtra ?? 0) - this.scheduledQuantityForItem(itemId)
    );
  };

  // One pass over ALL assignments (the whole plant pours together, regardless
  // of which job is selected) into a `${platform}|${yyyy-MM-dd}` -> placements
  // map, each cell pre-sorted: precast platforms biggest-first, VDT lines
  // smallest-first, by lengthInches with cubicFeetPerPiece as the fallback
  // size until the length field is populated; stable tiebreak on mark number.
  //
  // Keyed exactly as before, so nothing downstream changes shape — but the
  // unit is now a PLACEMENT, not a mark, which is what lets one mark occupy
  // eight days or four forms on one Friday.
  get scheduleCellMap(): Map<string, MMScheduledPlacement[]> {
    const map = new Map<string, MMScheduledPlacement[]>();
    const items = this.itemsById;

    for (const assignment of this.scheduleAssignments) {
      const item = items.get(assignment.productionItemId);
      // A placement whose mark is gone, or which sits on a platform/day the
      // grid has no cell for, has nowhere to render. Skipping it here is the
      // same rule isPlacedOnSchedule applied to the scalar fields, so nothing
      // falls between the grid and the unscheduled panel.
      if (!item) continue;
      if (!assignment.platform || !SCHEDULE_PLATFORMS.has(assignment.platform))
        continue;
      if (!assignment.pourDate || !isPourDay(assignment.pourDate)) continue;

      const key = `${assignment.platform}|${assignment.pourDate}`;
      const cell = map.get(key);
      if (cell) cell.push({ assignment, item });
      else map.set(key, [{ assignment, item }]);
    }

    // In-cell order is JOB NUMBER first, then FORM NUMBER (Creede, 2026-08-14:
    // "lowest job number at the top, forms in order beneath"). The key used to
    // be piece SIZE (lengthInches ?? cubicFeetPerPiece, biggest-first for
    // precast and smallest-first for VDT lines), which is why the order read as
    // arbitrary to him: "here the C comes before the B, so I don't know what's
    // driving the logic behind that." Neither job nor form was in the old key.
    //
    // compareNaturally, not compareJobNumbers: the day sheet orders its job
    // groups with this same collator (printDailySchedule buildPrecastGroups)
    // and ScheduleGrid assigns job colours with it. Creede reads the grid and
    // the printed sheet side by side, so all three have to agree — matching the
    // API's ORDER BY matters less here than matching the paper.
    //
    // Dropping the size comparison is near-inconsequential once one-piece-per-
    // form-per-day is enforced: a cell then holds at most one placement per
    // form, so the form key resolves nearly every comparison before size could
    // have. It does change printed day-sheet row order (the sheet inherits this
    // ordering) — within a job group rows now read in form order, which is what
    // a form-oriented sheet wants. Flag it on the review call anyway.
    const projectStore = this.rootStore.mmProjectStore;
    const jobNumberOf = (item: MMProductionItem): string =>
      String(projectStore?.getProjectById(item.projectId)?.jobNumber ?? "");

    // VDT, TODAY AND LATER: the order he put them there (09-30 item 2). It is
    // the crew's change order -- "when he packs this B07, the next thing he's
    // pack is a 12, and then the next thing is an 11." Past days and precast
    // keep the sort below: "once a day has passed, you can then retroactively
    // sort them", and precast "functions exactly like I want it."
    const today = todayDateOnly();
    for (const [key, placements] of map) {
      const [platform, date] = key.split("|");
      if (productTypeOfPlatform(platform) === "vdt" && date >= today) {
        placements.sort((a, b) => compareDragOrder(a.assignment, b.assignment));
        continue;
      }
      placements.sort((a, b) => {
        const byJob = compareNaturally(jobNumberOf(a.item), jobNumberOf(b.item));
        if (byJob !== 0) return byJob;

        const byForm = compareNaturally(
          a.assignment.formId ?? "",
          b.assignment.formId ?? ""
        );
        if (byForm !== 0) return byForm;

        // Both halves of a double share one formId, so without this they tie on
        // form and fall through to mark number — which put F2-B above F2-A
        // whenever the B-side mark happened to sort first. The form's own halves
        // must read left-to-right.
        const bySide = (a.assignment.side ?? "").localeCompare(
          b.assignment.side ?? ""
        );
        if (bySide !== 0) return bySide;

        return markNumberOrder.compare(a.item.markNumber, b.item.markNumber);
      });
    }
    return map;
  }

  // Placements the grid has no cell for — an unknown platform, or a Sunday.
  // These still consume the mark's remainder (the server counts them too), so
  // without surfacing them a mark could disappear from the grid AND the
  // unscheduled panel at once, with no way to recover it from the UI. That is
  // the failure `isPlacedOnSchedule` was written to prevent for the scalar
  // fields, and it has to hold here too.
  //
  // The frontend cannot create one — platforms come from the grid rows and
  // `pourDayRun` never emits a Sunday — but the 07-29 backfill could have,
  // from marks carrying a retired platform name or an odd date.
  get orphanedPlacements(): MMScheduledPlacement[] {
    const items = this.itemsById;
    const orphans: MMScheduledPlacement[] = [];
    for (const assignment of this.scheduleAssignments) {
      const item = items.get(assignment.productionItemId);
      if (!item) continue;
      if (this.selectedProjectId && item.projectId !== this.selectedProjectId)
        continue;
      const renderable =
        !!assignment.platform &&
        SCHEDULE_PLATFORMS.has(assignment.platform) &&
        !!assignment.pourDate &&
        isPourDay(assignment.pourDate);
      if (!renderable) orphans.push({ assignment, item });
    }
    return orphans;
  }

  // Pieces the whole plant pours on a day, across every platform and line.
  // Lets the print picker show which days are empty BEFORE you print them,
  // rather than discovering it at the printer.
  // On one product's rows, when asked: a screen's print picker counts only what
  // that screen prints (09-30 item 6).
  scheduledPiecesOn = (dateOnly: string, productType: MarkProductType | "all" = "all"): number =>
    SCHEDULE_ROWS.filter((row) => productType === "all" || row.productType === productType).reduce(
      (sum, row) =>
        sum +
        this.getScheduleCellPlacements(row.platform, dateOnly).reduce(
          (daySum, { assignment }) => daySum + assignment.quantity,
          0
        ),
      0
    );

  /**
   * Pieces ONE job has scheduled on a day, across every platform.
   *
   * Feeds the run-out day picker, so he can see which day he actually arranged
   * before running it forward. A day with nothing on it cannot seed a run-out,
   * and the picker says so rather than offering a button that does nothing.
   */
  scheduledPiecesOnForProject = (dateOnly: string, projectId: string): number => {
    if (!projectId) return 0;
    const date = toDateOnly(dateOnly);
    return this.scheduleAssignments
      .filter(
        (a) =>
          a.pourDate === date &&
          this.projectIdForItem(a.productionItemId) === projectId
      )
      .reduce((sum, a) => sum + a.quantity, 0);
  };

  getScheduleCellPlacements = (
    platform: string,
    dateOnly: string
  ): MMScheduledPlacement[] => {
    return this.scheduleCellMap.get(`${platform}|${dateOnly}`) || NO_PLACEMENTS;
  };

  // Marks on the selected project with pieces still to place, and how many —
  // the panel reads "WD01 — 6 of 8 left" rather than hiding a mark the moment
  // one of its eight pieces is scheduled. This is the form-first half Creede
  // actually works from.
  get unscheduledItemsByProject(): MMUnscheduledMark[] {
    if (!this.selectedProjectId) return [];
    // One definition of "left to schedule" -- it used to be repeated here
    // inline, which is how a recast piece could be schedulable in one place
    // and missing from this list.
    return this.productionItems
      .filter((p) => p.projectId === this.selectedProjectId)
      .map((item) => ({ item, remaining: this.remainingQuantityForItem(item.id) }))
      .filter(({ remaining }) => remaining > 0)
      .sort((a, b) =>
        markNumberOrder.compare(a.item.markNumber, b.item.markNumber)
      );
  }

  // ========================================
  // ACTIONS - SELECTION
  // ========================================

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setSelectedProjectId = (id: string | null) => {
    this.selectedProjectId = id;
    this.selectedFormId = null;
    // Item 13: land on the newest phase, which is the one being produced.
    // Creede: "this phase is 89 pieces. I'm not worried about these eight
    // pieces I shipped two months ago." A job with no phases gets null, which
    // is "All phases" and is indistinguishable from today's behaviour.
    const phases = this.phasesForProject(id ?? "");
    // The LAST entry, which since 2026-09-08 means the highest-numbered phase
    // rather than the latest-dated one -- `phasesForProject` now sorts mockup
    // first, then naturally by name. That is still the phase he wants to open
    // on, and a mockup sorting first is exactly why it must be read from the
    // end and not the start.
    this.selectedPhase = phases.length > 0 ? phases[phases.length - 1] : null;
  };

  // null = All phases. A job with no phases stays here permanently.
  setSelectedPhase = (phase: string | null) => {
    this.selectedPhase = phase;
  };

  // Every phase on a job, mockup first and the rest in natural order, so the
  // highest-numbered phase is last -- which is the one the header opens on.
  // See the sort below for why that replaced ordering by earliest ticket date.
  //
  // Keyed case-insensitively, because a phase name is free text he types by
  // hand: job 3938 is all "pHASE 1", a shift-key slip that reached thirteen
  // groups. "Phase 1" typed on the fourteenth is the SAME phase — form
  // identity already reads it that way (phaseKey) — so listing both would
  // split one job's totals in the header while the scheduler treated them as
  // one. The spelling shown is the one the phase's earliest group carries.
  phasesForProject = (projectId: string): string[] => {
    // Which SPELLING of a case-variant phase name to show: the first group that
    // used it. Ordered by creation (`sortOrder`, which is assigned max+1), NOT
    // by ticket date -- since 2026-09-15 a new group has no ticket date, and an
    // empty string compares smaller than every real date, so a dated group would
    // have lost the spelling to an undated one purely by being older.
    const earliest = new Map<string, { label: string; rank: number }>();
    for (const group of this.ticketGroups) {
      if (group.projectId !== projectId) continue;
      const phase = (group.phase ?? "").trim();
      if (!phase) continue;
      const key = phaseKey(phase);
      const seen = earliest.get(key);
      if (!seen || group.sortOrder < seen.rank) {
        earliest.set(key, { label: phase, rank: group.sortOrder });
      }
    }
    // Mockup first, then naturally by name. Creede, 2026-09-04: "mock-up or
    // MU… is going to come first. And then phases will just be sequential by
    // number." Mockup-first is the EXCEPTION to his standing alphabetical-
    // then-numeric preference, and that preference is what orders the rest.
    //
    // By label, not by earliest ticket date: date only put a mockup first by
    // accident, when its paperwork happened to predate phase 1's. Natural
    // collation keeps "Phase 10" after "Phase 2", and he says phases are never
    // added out of order, so date and number agree in every case he described.
    //
    // `date` still decides which SPELLING is shown -- the earliest group's --
    // so job 3938 keeps reading "pHASE 1".
    const isMockup = (label: string) => /^(mu|mock)/i.test(label.trim());
    return [...earliest.values()]
      .sort((a, b) => {
        const mockA = isMockup(a.label);
        const mockB = isMockup(b.label);
        if (mockA !== mockB) return mockA ? -1 : 1;
        return compareNaturally(a.label, b.label);
      })
      .map(({ label }) => label);
  };

  // Does this ticket group belong to what the header is showing? Everything,
  // when no phase is selected. A group with no phase belongs to a job that has
  // none, so it is only hidden once a real phase is chosen. Compared through
  // phaseKey so a group spelled "Phase 1" is not hidden by a header sitting on
  // "pHASE 1" — see phasesForProject.
  private groupInSelectedPhase = (group: MMTicketGroup): boolean => {
    if (this.selectedPhase == null) return true;
    return phaseKey(group.phase) === phaseKey(this.selectedPhase);
  };

  // The same question for a mark, via its group. A mark with no group has no
  // phase and shows under "All phases" only.
  private itemInSelectedPhase = (item: MMProductionItem): boolean => {
    if (this.selectedPhase == null) return true;
    const group = this.ticketGroups.find((g) => g.id === item.ticketGroupId);
    return phaseKey(group?.phase) === phaseKey(this.selectedPhase);
  };

  setSelectedFormId = (id: string | null) => {
    this.selectedFormId = id;
  };

  setProductionTab = (
    tab: "tickets" | "forms" | "anchors" | "schedule" | "vdt" | "plant"
  ) => {
    this.productionTab = tab;
  };

  // ========================================
  // ACTIONS - TICKET GROUPS
  // ========================================

  createTicketGroup = async (
    typed: Omit<MMTicketGroup, "id">
  ): Promise<MMTicketGroup> => {
    const data = withNormalisedPhase(typed);
    try {
      const result: any = await this.ticketGroupsService.create({
        projectId: data.projectId,
        name: data.name,
        // Only sent when there is one. A new group has none -- inventing today's
        // date would put a fact in the record that nobody stated.
        ...(data.ticketDate
          ? { ticketDate: toDateOnly(data.ticketDate) }
          : {}),
        formNumbers: JSON.stringify(data.formNumbers),
        phase: data.phase || null,
        color: data.color || null,
        sortOrder: data.sortOrder,
      });

      const newGroup: MMTicketGroup = {
        id: result.ticketGroupId,
        projectId: result.projectId,
        name: result.name,
        ticketDate: toDateOnly(result.ticketDate) || undefined,
        formNumbers:
          typeof result.formNumbers === "string"
            ? JSON.parse(result.formNumbers)
            : result.formNumbers || [],
        phase: result.phase || undefined,
        color: result.color || undefined,
        sortOrder: Number(result.sortOrder) || 0,
      };

      runInAction(() => {
        this.ticketGroups.push(newGroup);
        this.expandedGroupIds.push(newGroup.id);
      });
      return newGroup;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to create ticket group";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create ticket group"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  updateTicketGroup = async (
    id: string,
    typed: Partial<Omit<MMTicketGroup, "id">>
  ): Promise<boolean> => {
    const data = withNormalisedPhase(typed);
    try {
      const patchData: any = { ...data };
      if (data.formNumbers) {
        patchData.formNumbers = JSON.stringify(data.formNumbers);
      }
      // Normalize to yyyy-MM-dd — the API rejects a full datetime for the
      // `format: 'date'` ticketDate, which otherwise aborts the whole edit.
      if (data.ticketDate != null) {
        patchData.ticketDate = toDateOnly(data.ticketDate);
      }

      await this.ticketGroupsService.patch(id, patchData);

      runInAction(() => {
        const index = this.ticketGroups.findIndex((g) => g.id === id);
        if (index !== -1) {
          this.ticketGroups[index] = {
            ...this.ticketGroups[index],
            ...data,
            ...(data.ticketDate != null
              ? { ticketDate: toDateOnly(data.ticketDate) }
              : {}),
          };
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update ticket group";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update ticket group"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteTicketGroup = async (id: string): Promise<boolean> => {
    try {
      // Ungroup production items that belong to this group
      const itemsInGroup = this.productionItems.filter(
        (i) => i.ticketGroupId === id
      );
      if (itemsInGroup.length > 0) {
        await Promise.all(
          itemsInGroup.map((item) =>
            this.productionItemsService.patch(item.id, { ticketGroupId: "" })
          )
        );
      }

      await this.ticketGroupsService.remove(id);

      runInAction(() => {
        this.ticketGroups = this.ticketGroups.filter((g) => g.id !== id);
        this.productionItems.forEach((item) => {
          if (item.ticketGroupId === id) {
            item.ticketGroupId = "";
          }
        });
        this.expandedGroupIds = this.expandedGroupIds.filter(
          (gid) => gid !== id
        );
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete ticket group";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete ticket group"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  toggleGroupExpanded = (groupId: string) => {
    const idx = this.expandedGroupIds.indexOf(groupId);
    if (idx >= 0) {
      this.expandedGroupIds.splice(idx, 1);
    } else {
      this.expandedGroupIds.push(groupId);
    }
  };

  expandAllGroups = () => {
    this.expandedGroupIds = this.ticketGroupsByProject.map((g) => g.id);
  };

  collapseAllGroups = () => {
    this.expandedGroupIds = [];
  };

  isGroupExpanded = (groupId: string): boolean => {
    return this.expandedGroupIds.includes(groupId);
  };

  // ========================================
  // ACTIONS - FORMS
  // ========================================

  /**
   * A form group made in one go (09-30 item 11). Creede: "add F21 and have an
   * option for like number of forms in this form group because they're going
   * to all share this exact same information... I give him this whole group."
   *
   * The first form is the group's base and carries the hours -- "the hours are
   * for all three of these forms" -- so the others are linked copies with none
   * of their own, and the Forms tab's totals count the group once. Numbers run
   * on from the first (F21 -> F22, F23...); a form added to the group later
   * ("it may add a 3A") is a single copy, as before.
   */
  createFormGroup = async (
    data: Omit<MMForm, "id">,
    count: number
  ): Promise<MMForm[] | null> => {
    const numbers = sequentialFormNumbers(data.formNumber, count);
    if (!numbers) {
      this.rootStore.notificationStore?.addNotification({
        message: `"${data.formNumber}" doesn't end in a number, so the rest of the group can't be numbered from it.`,
        options: { variant: "warning" },
      });
      return null;
    }
    const taken = numbers.filter((number) =>
      this.forms.some(
        (form) =>
          form.projectId === data.projectId &&
          formKey(form.formNumber) === formKey(number) &&
          phaseKey(form.phase) === phaseKey(data.phase)
      )
    );
    if (taken.length > 0) {
      this.rootStore.notificationStore?.addNotification({
        message: `This job already has ${taken.join(", ")}. Start the group after them.`,
        options: { variant: "warning" },
      });
      return null;
    }

    const base = await this.createForm({ ...data, formNumber: numbers[0] });
    const group = [base];
    for (const formNumber of numbers.slice(1)) {
      group.push(
        await this.createForm({
          ...data,
          formNumber,
          baseFormId: base.id,
          plantManagerHours: 0,
          estimatorHours: undefined,
          actualHours: undefined,
        })
      );
    }
    return group;
  };

  createForm = async (typed: Omit<MMForm, "id">): Promise<MMForm> => {
    const data = withNormalisedPhase(typed);
    try {
      const createData: Record<string, unknown> = {
        projectId: data.projectId,
        formNumber: data.formNumber,
        plantManagerHours: data.plantManagerHours,
        status: data.status,
      };
      // Which phase's steel this is. Omitted rather than sent as null when the
      // job has no phases, so an unphased job writes exactly what it used to.
      if (data.phase) createData.phase = data.phase;
      if (data.estimatorHours != null)
        createData.estimatorHours = data.estimatorHours;
      if (data.baseFormId) createData.baseFormId = data.baseFormId;
      if (data.actualHours != null) createData.actualHours = data.actualHours;
      if (data.builder) createData.builder = data.builder;
      createData.isDouble = !!data.isDouble;
      if (data.givenToMoldShopDate)
        createData.givenToMoldShopDate = toDateOnly(data.givenToMoldShopDate);
      if (data.completedDate)
        createData.completedDate = toDateOnly(data.completedDate);

      const result: any = await this.formsService.create(createData);

      const newForm: MMForm = {
        id: result.formId,
        projectId: result.projectId,
        formNumber: result.formNumber,
        phase: result.phase || undefined,
        isDouble: !!result.isDouble,
        baseFormId: result.baseFormId || undefined,
        estimatorHours:
          result.estimatorHours != null
            ? Number(result.estimatorHours)
            : undefined,
        plantManagerHours: Number(result.plantManagerHours) || 0,
        actualHours:
          result.actualHours != null ? Number(result.actualHours) : undefined,
        status: result.status || "pending",
        builder: result.builder || undefined,
        givenToMoldShopDate:
          toDateOnly(result.givenToMoldShopDate) || undefined,
        completedDate: toDateOnly(result.completedDate) || undefined,
      };

      runInAction(() => {
        this.forms.push(newForm);
      });
      return newForm;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create form";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create form"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  updateForm = async (
    id: string,
    typed: Partial<Omit<MMForm, "id">>,
    options?: { applyToGroup?: boolean }
  ): Promise<boolean> => {
    const data = withNormalisedPhase(typed);
    try {
      // Filter out empty strings and undefined for optional fields — EXCEPT
      // the fields the API accepts an explicit null for. Skipping those would
      // make "clear the value I set earlier" a silent no-op: the patch would
      // omit the key, the server would keep the old number, and the field
      // would repopulate on the next load with no error anywhere.
      // `actualHours` is deliberately NOT in this set — its column is
      // Type.Optional(Number) with no null arm, so sending null would 400.
      const NULLABLE_FIELDS = new Set(["estimatorHours"]);
      const patchData: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (value === undefined || value === "") {
          if (NULLABLE_FIELDS.has(key)) patchData[key] = null;
          continue;
        }
        patchData[key] = value;
      }

      // Normalize DATE fields to yyyy-MM-dd — the API rejects a full datetime
      // for `format: 'date'` columns, which would otherwise abort the edit.
      for (const field of ["givenToMoldShopDate", "completedDate"]) {
        if (patchData[field] != null) {
          patchData[field] = toDateOnly(patchData[field]);
        }
      }

      await this.formsService.patch(id, patchData);

      runInAction(() => {
        const index = this.forms.findIndex((f) => f.id === id);
        if (index !== -1) {
          this.forms[index] = { ...this.forms[index], ...data };
        }
      });

      // The whole group follows ONLY when asked (2026-10-07). This used to be
      // automatic, so completing the first form completed all ten -- Creede:
      // "Sometimes the whole thing's completed, but sometimes not... So I need
      // to be able to do both." Hours stay on the first form either way.
      const shared: Record<string, unknown> = {};
      for (const field of ["status", "builder", "givenToMoldShopDate", "completedDate"]) {
        if (field in patchData) shared[field] = patchData[field];
      }
      const copies = options?.applyToGroup ? this.getFormCopies(id) : [];
      if (copies.length > 0 && Object.keys(shared).length > 0) {
        for (const copy of copies) {
          await this.formsService.patch(copy.id, shared);
        }
        runInAction(() => {
          this.forms = this.forms.map((form) =>
            form.baseFormId === id ? { ...form, ...(shared as Partial<MMForm>) } : form
          );
        });
      }
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update form";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update form"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteForm = async (id: string): Promise<boolean> => {
    try {
      await this.formsService.remove(id);

      runInAction(() => {
        this.forms = this.forms.filter((f) => f.id !== id);
        if (this.selectedFormId === id) {
          this.selectedFormId = null;
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to delete form";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete form"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // ACTIONS - PRODUCTION ITEMS
  // ========================================

  createProductionItem = async (
    data: Omit<
      MMProductionItem,
      "id" | "totalCubicFeet" | "lbsPerPiece" | "percentProduced" | "percentDelivered"
    >
  ): Promise<MMProductionItem> => {
    const totalCuFt = data.quantity * data.cubicFeetPerPiece;
    const lbs = data.cubicFeetPerPiece * lbsPerCuFt(data.productType);
    const percentProduced =
      data.quantity > 0 ? (data.piecesProduced / data.quantity) * 100 : 0;
    const percentDelivered =
      data.quantity > 0 ? (data.piecesDelivered / data.quantity) * 100 : 0;

    try {
      const createData: Record<string, unknown> = {
        projectId: data.projectId,
        ticketGroupId: data.ticketGroupId,
        markNumber: data.markNumber,
        productType: data.productType,
        quantity: data.quantity,
        cubicFeetPerPiece: data.cubicFeetPerPiece,
        totalCubicFeet: Math.round(totalCuFt * 100) / 100,
        lbsPerPiece: Math.round(lbs),
        platform: data.platform,
        qtyPerForm: data.qtyPerForm,
        isDouble: data.isDouble,
        piecesProduced: data.piecesProduced,
        piecesDelivered: data.piecesDelivered,
        percentProduced: Math.round(percentProduced * 100) / 100,
        percentDelivered: Math.round(percentDelivered * 100) / 100,
      };
      if (data.formId) createData.formId = data.formId;
      if (data.piecesWashed != null) createData.piecesWashed = data.piecesWashed;
      if (data.piecesExtra != null) createData.piecesExtra = data.piecesExtra;
      if (data.notes) createData.notes = data.notes;
      if (data.lengthInches != null)
        createData.lengthInches = data.lengthInches;
      if (data.scheduledPourDate)
        createData.scheduledPourDate = toDateOnly(data.scheduledPourDate);
      if (data.ticketDate) createData.ticketDate = toDateOnly(data.ticketDate);
      if (data.issueDate) createData.issueDate = toDateOnly(data.issueDate);
      if (data.formCompletedDate)
        createData.formCompletedDate = toDateOnly(data.formCompletedDate);
      if (data.productionCompletedDate)
        createData.productionCompletedDate = toDateOnly(
          data.productionCompletedDate
        );

      const result: any = await this.productionItemsService.create(createData);

      const newItem: MMProductionItem = {
        ...data,
        id: result.productionItemId,
        totalCubicFeet: Math.round(totalCuFt * 100) / 100,
        lbsPerPiece: Math.round(lbs),
        percentProduced: Math.round(percentProduced * 100) / 100,
        percentDelivered: Math.round(percentDelivered * 100) / 100,
      };

      runInAction(() => {
        this.productionItems.push(newItem);
      });
      return newItem;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to create production item";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create production item"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  // Item 14 — record a recast or QC failure against a mark.
  //
  // Creede's Excel process, which this replaces: a dated comment on the cell
  // carrying the DATE, the NUMBER OF PIECES and the REASON, with the remake
  // entered as an extra piece. Both halves happen here, in one write.
  //
  // APPENDS. His comments accumulate — on a 200-piece mark, piece 10 and piece
  // 200 might both be scrapped months apart — and an overwrite would destroy
  // exactly the history that makes the field worth keeping. Newest LAST, so the
  // column reads chronologically the way his cells do.
  //
  // `piecesExtra` is incremented rather than set, because it is a running count
  // of remakes and a second recast must not erase the first one's piece.
  /**
   * Write a note at one of the three scopes. One entry point, as Creede asked:
   * "this note we're going to categorize under, it's a project-wide note; this
   * one is specific to WEO2."
   *
   * The author is stamped SERVER-side from the authenticated user, not passed
   * from here -- an author the client can set is not an author, and three
   * people write these today.
   */
  createNote = async (data: {
    scope: MMNote["scope"];
    projectId?: string;
    ticketGroupId?: string;
    productionItemId?: string;
    body: string;
    kind?: "note" | "recast";
    pieces?: number;
    occurredOn?: string;
  }): Promise<MMNote | null> => {
    const body = data.body.trim();
    if (!body) return null;
    try {
      const created = await this.notesService.create({
        scope: data.scope,
        // Exactly one target, matching the scope. The database enforces this
        // too (ck_mmNotes_one_scope) -- sending a second one is refused there,
        // not silently stored.
        projectId: data.scope === "project" ? data.projectId : undefined,
        ticketGroupId: data.scope === "ticketGroup" ? data.ticketGroupId : undefined,
        productionItemId: data.scope === "mark" ? data.productionItemId : undefined,
        body,
        kind: data.kind || "note",
        pieces: data.pieces,
        occurredOn: data.occurredOn ? toDateOnly(data.occurredOn) : undefined,
      });
      const note = mapNote(created);
      runInAction(() => {
        this.notes.push(note);
      });
      return note;
    } catch (error) {
      this.reportScheduleError(error, "Failed to save the note");
      return null;
    }
  };

  // Deleting a RECAST also takes back the extra pieces it added (2026-10-16
  // item 21). Creede: "I was able to delete the QC issue… but I can't delete
  // this extra piece… let's just be able to delete it." `recordRecast` writes
  // the note AND bumps `piecesExtra`; removing only the note left the remake
  // behind with nothing to explain it. The note goes first, so a failed delete
  // changes no count.
  deleteNote = async (noteId: string): Promise<boolean> => {
    const note = this.notes.find((n) => n.id === noteId);
    try {
      await this.notesService.remove(noteId);
      runInAction(() => {
        this.notes = this.notes.filter((n) => n.id !== noteId);
      });
    } catch (error) {
      this.reportScheduleError(error, "Failed to delete the note");
      return false;
    }
    if (note?.kind === "recast" && note.productionItemId && (note.pieces ?? 0) > 0) {
      const item = this.productionItems.find((p) => p.id === note.productionItemId);
      if (item) {
        return this.updateProductionItem(item.id, {
          piecesExtra: Math.max(0, (item.piecesExtra ?? 0) - (note.pieces ?? 0)),
        });
      }
    }
    return true;
  };

  /** Notes at one scope, oldest first, which is how a running log reads. */
  /**
   * How a note's author is shown. Notes written from 2026-09-16 carry a name
   * already; older ones carry the email login, and Creede asked to see names
   * either way. Falls back to whatever was stamped -- never to a blank, because
   * "who wrote this" is the point of the field.
   */
  authorLabel = (author?: string | null): string => {
    const stamped = (author ?? "").trim();
    if (!stamped) return "";
    if (!stamped.includes("@")) return stamped;
    return this.authorNamesByEmail.get(stamped.toLowerCase()) ?? stamped;
  };

  notesFor = (scope: MMNote["scope"], id?: string): MMNote[] => {
    if (!id) return [];
    const key =
      scope === "project"
        ? "projectId"
        : scope === "ticketGroup"
        ? "ticketGroupId"
        : "productionItemId";
    return this.notes
      .filter((note) => note.scope === scope && note[key] === id)
      .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  };

  /**
   * Record a recast or QC failure against a mark.
   *
   * Now a NOTE ROW rather than a line appended to `mmProductionItems.notes`.
   * The blob was always a stopgap -- its own migration says a child table "is
   * the right answer if this stops being interim" -- and its limits are exactly
   * what breaks once notes exist at three scopes: no author, no entry
   * timestamp, and no way to remove one entry without rewriting the lot. The
   * pieces and date are stored as COLUMNS here, so the recast log is queryable
   * instead of being parsed back out of prose.
   *
   * `piecesExtra` is still incremented, unchanged: that is Creede's "enter the
   * remake as an extra piece", and it is what the Extra column reads.
   */
  recordRecast = async (
    itemId: string,
    entry: { date: string; pieces: number; reason: string }
  ): Promise<boolean> => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return false;

    const reason = entry.reason.trim();
    const date = toDateOnly(entry.date);
    const pieces = Math.floor(entry.pieces);
    // A reason is the whole point of the record, and a count of zero is not a
    // recast. Refuse rather than write a line that says nothing.
    //
    // And SAY so. The date is typed into a prompt, and "9/2/2026" is what a
    // person types: it was refused here in silence, so the recast he believed
    // he had recorded simply was not there.
    if (!reason || !date || pieces <= 0) {
      this.rootStore.notificationStore?.addNotification({
        message: !date
          ? `Recast not recorded: "${entry.date}" is not a date this can read. Enter it as YYYY-MM-DD.`
          : "Recast not recorded: it needs a reason and at least one piece.",
        options: { variant: "warning" },
      });
      return false;
    }

    const note = await this.createNote({
      scope: "mark",
      productionItemId: itemId,
      body: reason,
      kind: "recast",
      pieces,
      occurredOn: date,
    });
    if (!note) return false;

    return this.updateProductionItem(itemId, {
      piecesExtra: (item.piecesExtra ?? 0) + pieces,
    });
  };

  /**
   * The recast lines on a mark, oldest first, for rendering.
   *
   * Reads BOTH the note rows and the legacy `notes` blob, so nothing recorded
   * before this shipped disappears the day it does. There is deliberately no
   * backfill: merging costs one map and a concat, while parsing prose out of
   * live production data to move it is a risk with no upside. Once the blob
   * stops gaining lines it can be retired on its own.
   */
  recastEntries = (item: MMProductionItem): string[] => {
    const legacy = (item.notes ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const recorded = this.notesFor("mark", item.id)
      .filter((note) => note.kind === "recast")
      .map(
        (note) =>
          `${note.occurredOn ?? ""} — ${note.pieces ?? 0} pc — ${note.body}${
            note.author ? ` (${note.author})` : ""
          }`
      );
    return [...legacy, ...recorded];
  };

  /**
   * The recasts recorded against a mark ON ONE DAY (2026-10-16 item 20).
   * Creede: "It is giving me a QC error on every day that pours that mark. I
   * would only want that to show up on the day in which the error occurred."
   * Only dated note rows qualify; the legacy `notes` blob carries no date this
   * can trust, so it shows on no day -- it stays in the mark's own notes.
   */
  recastEntriesOn = (item: MMProductionItem, date: string): string[] =>
    this.notesFor("mark", item.id)
      .filter((note) => note.kind === "recast" && note.occurredOn === date)
      .map(
        (note) =>
          `${note.occurredOn ?? ""} — ${note.pieces ?? 0} pc — ${note.body}${
            note.author ? ` (${note.author})` : ""
          }`
      );

  /**
   * Cu.ft of a job's pieces that failed QC on `date` (item 20): Σ recast
   * pieces × the mark's cu.ft per piece. The daily report takes it out of the
   * VALUE produced -- "keep it in a sense of how many cubic feet are
   * produced… but remove it for our value produced."
   */
  recastCubicFeetOn = (projectId: string, date: string): number => {
    let total = 0;
    for (const note of this.notes) {
      if (note.kind !== "recast" || note.occurredOn !== date || !note.productionItemId) continue;
      const item = this.productionItems.find((p) => p.id === note.productionItemId);
      if (!item || item.projectId !== projectId) continue;
      total += (note.pieces ?? 0) * (item.cubicFeetPerPiece || 0);
    }
    return total;
  };

  // ========================================
  // MOCK-UP RATE (2026-10-16 item 22)
  // ========================================
  //
  // Creede: "any phase that is mock-up should be using the mock-up billing
  // amount for its billing rate… daily report… billing for loads… it's like a
  // mini job in a job… If I've got mock-up tickets, use that total cubic feet
  // of the mock-up to divide out your billing amount… no need for production
  // tickets final… for the mock-up."
  //
  // A mock-up is a mark whose ticket group's phase is one (/^(mu|mock)/i, the
  // same test as everywhere else). The MAIN job rate is unchanged: whether the
  // job's total cubic feet includes the mock-up's (D6) is still Creede's call.

  isMockupItem = (item: Pick<MMProductionItem, "ticketGroupId">): boolean => {
    const phase = item.ticketGroupId ? this.getTicketGroupById(item.ticketGroupId)?.phase : undefined;
    return !!phase && /^(mu|mock)/i.test(phase.trim());
  };

  /** The mock-up's cubic feet, from its tickets -- no "final" gate. */
  mockupCubicFeet = (projectId: string): number =>
    Math.round(
      this.productionItems
        .filter((item) => item.projectId === projectId && this.isMockupItem(item))
        .reduce((sum, item) => sum + (item.quantity || 0) * (item.cubicFeetPerPiece || 0), 0) * 100
    ) / 100;

  /**
   * Mock-up allocation ÷ the mock-up's cubic feet, or null when there is no
   * allocation or no mock-up ticketed yet -- its marks then bill at the job's
   * rate, as they always have.
   */
  mockupRateFor = (projectId: string): number | null => {
    const project = this.rootStore.mmProjectStore?.getProjectById(projectId);
    const allocation = Number(project?.mockupAllocation) || 0;
    const cubicFeet = this.mockupCubicFeet(projectId);
    if (allocation <= 0 || cubicFeet <= 0) return null;
    return Math.round((allocation / cubicFeet) * 100) / 100;
  };

  /** Is this mark number, on this job, a mock-up piece? */
  isMockupMark = (projectId: string, markNumber: string): boolean => {
    const key = markNumber.trim().toUpperCase();
    const item = this.productionItems.find(
      (p) => p.projectId === projectId && p.markNumber.trim().toUpperCase() === key
    );
    return !!item && this.isMockupItem(item);
  };

  /** Cu.ft of a job's mock-up pieces produced on a day, for the daily report. */
  mockupCubicFeetProducedOn = (projectId: string, date: string): number => {
    let total = 0;
    for (const assignment of this.scheduleAssignments) {
      if (assignment.pourDate !== date) continue;
      const item = this.productionItems.find((p) => p.id === assignment.productionItemId);
      if (!item || item.projectId !== projectId || !this.isMockupItem(item)) continue;
      total += (assignment.piecesProduced || 0) * (item.cubicFeetPerPiece || 0);
    }
    return total;
  };

  // ========================================
  // VDT TEST BLOCKS (2026-10-16 item 26)
  // ========================================

  /**
   * A mark's mix number: the colour its ticket group pours (else the job's),
   * looked up on the job's colour list. Blank when the colour has no number.
   */
  mixNumberOfItem = (item: MMProductionItem): string => {
    const groupColor = item.ticketGroupId ? this.getTicketGroupById(item.ticketGroupId)?.color : undefined;
    const projectStore = this.rootStore.mmProjectStore;
    const color = groupColor?.trim() || projectStore?.getProjectById(item.projectId)?.concreteColor?.trim() || "";
    return projectStore?.mixNumberFor?.(item.projectId, color) ?? "";
  };

  /**
   * Toward the 250 cu ft test block, for one mix on one day. Creede: "Draw
   * this from the entered and completed production of previous days." VDT
   * only, across every job pouring the mix (D8 default: the block is per mix).
   * A block made on a day closes that day's count; the next starts the day
   * after.
   */
  testBlockTally = (mixNumber: string, date: string): MMTestBlockTally => {
    const mix = mixNumber.trim();
    const blocks = this.testBlocks.filter((b) => b.mixNumber === mix);
    const lastBlockOn =
      blocks
        .map((b) => b.madeOn)
        .filter((d) => d < date)
        .sort()
        .pop() ?? null;
    let beforeCuFt = 0;
    let todayProducedCuFt = 0;
    let todayScheduledCuFt = 0;
    for (const assignment of this.scheduleAssignments) {
      const day = assignment.pourDate;
      if (!day || day > date || (lastBlockOn && day <= lastBlockOn)) continue;
      const item = this.productionItems.find((p) => p.id === assignment.productionItemId);
      if (!item || item.productType !== "vdt" || this.mixNumberOfItem(item) !== mix) continue;
      const perPiece = item.cubicFeetPerPiece || 0;
      if (day === date) {
        todayProducedCuFt += (assignment.piecesProduced || 0) * perPiece;
        todayScheduledCuFt += (assignment.quantity || 0) * perPiece;
      } else {
        beforeCuFt += (assignment.piecesProduced || 0) * perPiece;
      }
    }
    const round = (n: number) => Math.round(n * 100) / 100;
    return {
      mixNumber: mix,
      lastBlockOn,
      beforeCuFt: round(beforeCuFt),
      todayProducedCuFt: round(todayProducedCuFt),
      todayScheduledCuFt: round(todayScheduledCuFt),
      blockMadeToday: blocks.some((b) => b.madeOn === date),
    };
  };

  /** The mix numbers VDT pours on a day, in first-seen order. */
  vdtMixNumbersOn = (date: string): string[] => {
    const mixes: string[] = [];
    for (const assignment of this.scheduleAssignments) {
      if (assignment.pourDate !== date) continue;
      const item = this.productionItems.find((p) => p.id === assignment.productionItemId);
      if (!item || item.productType !== "vdt") continue;
      const mix = this.mixNumberOfItem(item);
      if (mix && !mixes.includes(mix)) mixes.push(mix);
    }
    return mixes;
  };

  /** "Test block made" ticked: one per mix per day (the database agrees). */
  recordTestBlock = async (mixNumber: string, madeOn: string): Promise<boolean> => {
    const mix = mixNumber.trim();
    const day = toDateOnly(madeOn);
    if (!mix || !day) return false;
    if (this.testBlocks.some((b) => b.mixNumber === mix && b.madeOn === day)) return true;
    try {
      const created = await this.testBlocksService.create({ mixNumber: mix, madeOn: day });
      runInAction(() => {
        this.testBlocks.push({ id: created.testBlockId, mixNumber: mix, madeOn: toDateOnly(created.madeOn) || day });
      });
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to record the test block");
      return false;
    }
  };

  /** Unticked: the block was a mistake, and the count runs on from the one before. */
  removeTestBlock = async (mixNumber: string, madeOn: string): Promise<boolean> => {
    const block = this.testBlocks.find((b) => b.mixNumber === mixNumber.trim() && b.madeOn === madeOn);
    if (!block) return true;
    try {
      await this.testBlocksService.remove(block.id);
      runInAction(() => {
        this.testBlocks = this.testBlocks.filter((b) => b.id !== block.id);
      });
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to remove the test block");
      return false;
    }
  };

  updateProductionItem = async (
    id: string,
    data: Partial<MMProductionItem>
  ): Promise<boolean> => {
    const index = this.productionItems.findIndex((p) => p.id === id);
    if (index === -1) return false;

    const current = this.productionItems[index];
    const updated = { ...current, ...data };

    // Recalculate derived fields
    if (
      data.quantity !== undefined ||
      data.cubicFeetPerPiece !== undefined ||
      data.productType !== undefined
    ) {
      updated.totalCubicFeet =
        Math.round(updated.quantity * updated.cubicFeetPerPiece * 100) / 100;
      updated.lbsPerPiece = Math.round(
        updated.cubicFeetPerPiece * lbsPerCuFt(updated.productType)
      );
    }

    if (data.piecesProduced !== undefined || data.quantity !== undefined) {
      updated.percentProduced =
        updated.quantity > 0
          ? Math.round(
              (updated.piecesProduced / updated.quantity) * 10000
            ) / 100
          : 0;
    }

    // An edit is NOT a review. 2026-09-23 had it the other way ("if I go and
    // change this to 0.45... this check can go away"), and Creede reversed it
    // on 2026-10-16: the field commits half a second after he stops typing, so
    // the flag vanished while he was still looking back at the ticket -- "I
    // would ask that you retain it as needing to be checked until I hit
    // confirm." Only markProductionItemReviewed clears it now.

    if (data.piecesDelivered !== undefined || data.quantity !== undefined) {
      updated.percentDelivered =
        updated.quantity > 0
          ? Math.round(
              (updated.piecesDelivered / updated.quantity) * 10000
            ) / 100
          : 0;
    }

    try {
      // Build patch data with recalculated fields
      const { id: _id, ...patchData } = data;
      // Normalize DATE fields to yyyy-MM-dd — the API rejects a full datetime
      // for `format: 'date'` columns, which would otherwise abort the edit.
      for (const field of [
        "ticketDate",
        "issueDate",
        "formCompletedDate",
        "productionCompletedDate",
        "scheduledPourDate",
      ]) {
        if ((patchData as Record<string, unknown>)[field] != null) {
          (patchData as Record<string, unknown>)[field] = toDateOnly(
            (patchData as Record<string, unknown>)[field]
          );
        }
      }
      if (updated.totalCubicFeet !== current.totalCubicFeet) {
        (patchData as any).totalCubicFeet = updated.totalCubicFeet;
      }
      if (updated.lbsPerPiece !== current.lbsPerPiece) {
        (patchData as any).lbsPerPiece = updated.lbsPerPiece;
      }
      if (updated.percentProduced !== current.percentProduced) {
        (patchData as any).percentProduced = updated.percentProduced;
      }
      if (updated.percentDelivered !== current.percentDelivered) {
        (patchData as any).percentDelivered = updated.percentDelivered;
      }
      await this.productionItemsService.patch(id, patchData);

      runInAction(() => {
        this.productionItems[index] = updated;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update production item";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update production item"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  /**
   * "Nope, this is right" (Creede, 2026-09-23): clear the reader's doubt without
   * changing a value. sourcePages stays -- where the mark came from is still
   * true after it has been checked.
   */
  markProductionItemReviewed = async (id: string): Promise<boolean> => {
    const index = this.productionItems.findIndex((p) => p.id === id);
    if (index === -1) return false;
    try {
      await this.productionItemsService.patch(id, { reviewConfidence: null } as any);
      runInAction(() => {
        this.productionItems[index] = {
          ...this.productionItems[index],
          reviewConfidence: undefined,
        };
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to mark the row reviewed"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteProductionItem = async (id: string): Promise<boolean> => {
    try {
      await this.productionItemsService.remove(id);

      runInAction(() => {
        this.productionItems = this.productionItems.filter(
          (p) => p.id !== id
        );
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete production item";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete production item"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // Convenience methods for updating production counts
  // The daily report buckets solely on `productionCompletedDate`, which the API
  // stamps with the day of DATA ENTRY, in UTC, and never re-stamps. Entering on
  // Thursday a piece poured Tuesday credited Thursday — Creede: "it's keeping
  // that value on Monday… even though now I'm showing that it's produced on
  // Wednesday." Nothing in the request ever carried the real pour day, even
  // though the mark has `scheduledPourDate` sitting right next to it.
  //
  // The server resolver already honours an explicit client value ahead of its
  // own stamp, so sending the honest date is a frontend-only fix.
  //
  // Only stamped when the entry COMPLETES the mark, matching the API's own
  // threshold — a partial entry must not claim a completion date it has not
  // earned, or it would appear in the report as finished production.
  //
  // Known gap, deliberately not worked around: the column has no null arm
  // (`Type.Optional(Type.String({format:'date'}))`), so correcting a completed
  // mark back down to a partial count cannot clear the date from here. It
  // needs the backend to accept null.
  updateProducedCount = async (
    id: string,
    piecesProduced: number,
    producedDate?: string
  ): Promise<boolean> => {
    const item = this.productionItems.find((p) => p.id === id);
    const data: Partial<MMProductionItem> = { piecesProduced };
    const completesTheMark = !!item && piecesProduced >= item.quantity;
    // `scheduledPourDate` is no longer written by anything — scheduling moved
    // to assignments — so a mark scheduled the new way has none, and relying on
    // it alone would silently send no date at all. Prefer the placement the
    // mark actually pours on; fall back to the legacy column for marks still
    // carrying one from before the swap.
    const latestPlacementDate = (this.assignmentsByItemId.get(id) || [])
      .map((assignment) => assignment.pourDate)
      .filter(Boolean)
      .sort()
      .pop();
    const pourDate =
      producedDate ?? latestPlacementDate ?? item?.scheduledPourDate;
    if (completesTheMark && pourDate) {
      data.productionCompletedDate = pourDate;
    }
    return this.updateProductionItem(id, data);
  };

  // Correct which day a completed mark is credited to, independently of the
  // count. Surfaced next to the poured input so a back-dated entry is visible
  // and fixable rather than silently attributed to the day it was typed.
  updateProducedDate = async (
    id: string,
    productionCompletedDate: string
  ): Promise<boolean> => {
    if (!productionCompletedDate) return false;
    return this.updateProductionItem(id, { productionCompletedDate });
  };

  updateDeliveredCount = async (
    id: string,
    piecesDelivered: number
  ): Promise<boolean> => {
    return this.updateProductionItem(id, { piecesDelivered });
  };

  updateWashedCount = async (
    id: string,
    piecesWashed: number
  ): Promise<boolean> => {
    return this.updateProductionItem(id, { piecesWashed });
  };

  // Drag-and-drop form assignment from the unassigned pool. `formId` on a
  // production item holds the form NUMBER, not the mm-forms uuid (the
  // schema is a plain optional string, maxLength 50). `null` unassigns —
  // sent as "" on the wire, the same falsy convention deleteTicketGroup
  // uses for ticketGroupId. Goes through updateProductionItem: single
  // write path, error toast on failure.
  /**
   * Move a mark from the staging area into a ticket group, or back out of one.
   *
   * The staging area is not a new store: an imported mark is written with no
   * ticket group, which is what `ungroupedItems` has always meant. So the drop
   * is one field patch, through the single write path, and it survives a
   * refresh the way the unscheduled panel's marks do -- which is the analogy
   * Creede reached for when he asked for this.
   */
  assignMarkToGroup = async (
    itemId: string,
    ticketGroupId: string | null
  ): Promise<boolean> => {
    const current = this.productionItems.find((p) => p.id === itemId);
    if (!current) return false;
    const next = ticketGroupId ?? "";
    if ((current.ticketGroupId || "") === next) return true; // no-op drop
    return this.updateProductionItem(itemId, { ticketGroupId: next });
  };

  /**
   * The same move for a selection. A packet of tickets is a whole job's worth
   * of marks and they mostly land in one group, so doing this one drag at a
   * time is the thing that would send him back to Excel.
   *
   * One multi-patch, not N writes: the API's limiter allows 100 requests a
   * minute and a 400-mark job would spend four minutes of it. Feathers returns
   * an ARRAY from `patch(null, ...)`.
   */
  assignMarksToGroup = async (
    itemIds: string[],
    ticketGroupId: string | null
  ): Promise<number> => {
    const next = ticketGroupId ?? "";
    const moving = itemIds.filter((id) => {
      const item = this.productionItems.find((p) => p.id === id);
      return !!item && (item.ticketGroupId || "") !== next;
    });
    if (moving.length === 0) return 0;

    try {
      await this.productionItemsService.patch(
        null,
        { ticketGroupId: next },
        { query: { productionItemId: { $in: moving } } }
      );
      runInAction(() => {
        this.productionItems.forEach((item) => {
          if (moving.includes(item.id)) item.ticketGroupId = next;
        });
      });
      return moving.length;
    } catch (error) {
      this.reportScheduleError(error, "Failed to move those marks");
      return 0;
    }
  };

  // ========================================
  // ACTIONS - CASTING SCHEDULE
  // ========================================

  // Put a mark on the schedule: platform row + pour day. Also used for
  // cell-to-cell drags (rescheduling). Goes through updateProductionItem,
  // which normalizes the date and toasts on failure.
  // ========================================
  // SCHEDULE WRITES (assignments)
  // ========================================

  // Merge server rows into the local list without a refetch.
  private absorbAssignments = (rows: RawScheduleAssignment[]) => {
    runInAction(() => {
      for (const row of rows) {
        const index = this.scheduleAssignments.findIndex(
          (a) => a.id === row.assignmentId
        );
        if (index === -1) {
          this.scheduleAssignments.push(toAssignment(row));
          continue;
        }
        // MERGE onto the existing row rather than replacing it. A response
        // that omits a field would otherwise blank it locally — a patch reply
        // carrying only the field that changed would wipe `pourDate` and
        // `quantity`, which are what the grid and the remainder are built on.
        const current = this.scheduleAssignments[index];
        this.scheduleAssignments[index] = toAssignment({
          assignmentId: row.assignmentId,
          productionItemId: row.productionItemId ?? current.productionItemId,
          formId: row.formId !== undefined ? row.formId : current.formId,
          platform: row.platform !== undefined ? row.platform : current.platform,
          pourDate: row.pourDate !== undefined ? row.pourDate : current.pourDate,
          quantity: row.quantity !== undefined ? row.quantity : current.quantity,
          piecesProduced:
            row.piecesProduced !== undefined
              ? row.piecesProduced
              : current.piecesProduced,
          // Carried like every other field: this list is rebuilt field by
          // field, so omitting `side` would blank which half of a double a
          // piece pours in on the first move — and the one-piece-per-form-per-
          // day rule keys on it, so the next drop onto that day would then be
          // wrongly refused.
          side: row.side !== undefined ? row.side : current.side,
          sequence: row.sequence !== undefined ? row.sequence : current.sequence,
          createdAt: row.createdAt !== undefined ? row.createdAt : current.createdAt,
        });
      }
    });
  };

  // The server keeps mark.piecesProduced as the sum of its assignments, which
  // is what keeps the completion stamp and the daily report alive through the
  // transition. Mirror it locally so the UI does not need a refetch to agree.
  private syncItemProduced = (itemId: string) => {
    runInAction(() => {
      const index = this.productionItems.findIndex((p) => p.id === itemId);
      if (index === -1) return;
      const item = this.productionItems[index];
      const produced = (this.assignmentsByItemId.get(itemId) || []).reduce(
        (sum, assignment) => sum + assignment.piecesProduced,
        0
      );
      this.productionItems[index] = {
        ...item,
        piecesProduced: produced,
        // Good pieces, capped at 100%: an overage or a recast must not push a
        // mark past complete.
        percentProduced:
          item.quantity > 0
            ? Math.min(
                100,
                Math.round(
                  (goodPiecesOf({ piecesProduced: produced, piecesExtra: item.piecesExtra }) /
                    item.quantity) *
                    10000
                ) / 100
              )
            : 0,
      };
    });
  };

  // Carry the platform back from the schedule onto the mark.
  //
  // Creede sets platform to "unknown" when he creates a mark, because he does
  // not know it yet -- "I don't know what platform it's going in until I make a
  // palette" -- and expects scheduling to fill it in: "when I turn around and
  // schedule these forms... it should populate here to me." Nothing ever did.
  // The scheduling path writes assignments and stops; `platform` was only ever
  // written at mark creation.
  //
  // Derived, not asserted: the platform of the mark's EARLIEST placement, and
  // back to "unknown" when the last placement goes away. That second half also
  // closes the 07-27 follow-up where toggling product type left a stale
  // platform on a mark with no pour date.
  //
  // Same shape as stampCompletionDate below, including the no-op guard: a
  // twelve-form job would otherwise fire twelve identical patches into a
  // 100-req/min limiter that a single app boot already crowds.
  private syncItemPlatform = async (itemId: string): Promise<void> => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return;

    const placements = [...(this.assignmentsByItemId.get(itemId) || [])].sort(
      (a, b) => (a.pourDate || "").localeCompare(b.pourDate || "")
    );
    const next = placements.find((a) => !!a.platform)?.platform || UNKNOWN_PLATFORM;
    if (next === item.platform) return;

    try {
      await this.productionItemsService.patch(itemId, { platform: next });
      runInAction(() => {
        const index = this.productionItems.findIndex((p) => p.id === itemId);
        if (index !== -1) {
          this.productionItems[index] = {
            ...this.productionItems[index],
            platform: next,
          };
        }
      });
    } catch (error) {
      // Non-fatal, and deliberately quiet: the schedule is already saved and
      // correct. A failed convenience write must not make a placed piece look
      // unplaced, and a toast here would fire on every drag.
      console.error("Failed to carry the platform onto the mark:", error);
    }
  };

  // The server's remainder guard rejects an over-schedule with a message that
  // names the remainder — "only 8 of 50 remain unscheduled" — written to be
  // shown verbatim. Swallowing it and toasting "Bad Request" would throw away
  // the only actionable half.
  private reportScheduleError = (error: unknown, fallback: string) => {
    runInAction(() => {
      this.error = error instanceof Error ? error.message : fallback;
    });
    this.rootStore.notificationStore?.addNotification({
      message: mmErrorMessage(error, fallback),
      options: { variant: "error" },
    });
  };

  // THE headline behaviour of this cycle. Creede: "I should be able to drag
  // this piece here and it should schedule one piece in F1 for eight
  // consecutive pour days."
  //
  // Dropping a mark on a cell places its REMAINING pieces one per pour day
  // from that day forward, in one multi-create. Saturdays are skipped by
  // default with an override; the drop day itself is always honoured.
  // Everything stays hand-adjustable afterwards — dragging a placed chip moves
  // one day's placement, which is the flexibility he asked to keep.
  scheduleMarkRun = async (
    itemId: string,
    platform: string,
    startDateOnly: string,
    options?: {
      skipSaturdays?: boolean;
      perDay?: number;
      formId?: string;
      side?: DoubleSide;
      /**
       * The forms this mark pours in, one piece in each per day. Defaults to
       * `[formId]`, which is the old single-form behaviour.
       *
       * This is what item 3 of the 08-26 plan changes. Creede spreads a mark
       * across every interchangeable form in its group -- "WA10 in forms two
       * through five... I do take that and run that until that mark is done" --
       * and the old shape could not express it: one scalar `formId` was stamped
       * on every day of the run, so ten pieces became ten days in F1 and he
       * could not pull them back. "It's more regular than it not happening."
       */
      formIds?: string[];
      /** Pair each form with the half of a double it pours in. */
      sides?: (DoubleSide | undefined)[];
    }
  ): Promise<boolean> => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return false;

    const remaining = this.remainingQuantityForItem(itemId);
    if (remaining <= 0) {
      this.rootStore.notificationStore?.addNotification({
        message: `${item.markNumber} already has all ${item.quantity} pieces scheduled.`,
        options: { variant: "info" },
      });
      return false;
    }

    // One piece per form per day is the physical rule, so the number of forms
    // IS the daily rate. `perDay` stays for callers that place several pieces
    // in one form (dry pack), and the two multiply.
    const forms =
      options?.formIds && options.formIds.length > 0
        ? options.formIds
        : [options?.formId ?? item.formId ?? null];
    const sides = options?.sides ?? [];
    const perDay = Math.max(1, Math.floor(options?.perDay ?? 1));
    const perDayTotal = perDay * forms.length;
    const days = pourDayRun(
      toDateOnly(startDateOnly),
      Math.ceil(remaining / perDayTotal),
      options?.skipSaturdays ?? true,
      // Auto-populate steps over closed days, so a run started before a
      // holiday resumes after it instead of scheduling into a shut plant.
      this.closedDayKeys
    );

    // Production already recorded against the mark has to survive its first
    // scheduling. Once assignments exist the server derives
    // `mark.piecesProduced` from their sum, so creating rows that all start at
    // zero would patch a mark with five poured pieces down to zero — real
    // production destroyed in the client's live database.
    //
    // The server has a carry-forward hook for this, but it cannot help on a
    // multi-create: it checks "does this mark have assignments yet", which is
    // false for every row of the batch, so all eight would inherit the full
    // count and inflate 5 into 8. Distributing it here, earliest day first, is
    // both correct and what physically happened — five poured pieces means the
    // first five days' worth are done. An explicit value always wins over the
    // hook, so this takes it out of the equation.
    //
    // Only on FIRST scheduling: if the mark already has assignments, its
    // produced count is already the sum of them and new rows start at zero.
    const alreadyPlaced = this.scheduledQuantityForItem(itemId);
    let producedToCarry = alreadyPlaced === 0 ? item.piecesProduced || 0 : 0;

    // One row per (day x form). Chronological, so the produced carry-forward
    // above still distributes earliest-first onto the days that physically
    // happened.
    let left = remaining;
    const payload: Array<{
      productionItemId: string;
      formId: string | null;
      side: DoubleSide | null;
      platform: string;
      pourDate: string;
      quantity: number;
      piecesProduced: number;
    }> = [];

    for (const pourDate of days) {
      forms.forEach((formId, index) => {
        if (left <= 0) return;
        const quantity = Math.min(perDay, left);
        left -= quantity;
        const piecesProduced = Math.min(quantity, producedToCarry);
        producedToCarry -= piecesProduced;
        payload.push({
          productionItemId: itemId,
          // The human form NUMBER, not a uuid. Resolved at DROP time rather
          // than read off the mark: item.formId remains the fallback for a
          // pre-assigned mark, which is Creede's F1A escape hatch.
          formId,
          // Which half of a double form. null for a single pour.
          side: sides[index] ?? options?.side ?? null,
          platform,
          pourDate,
          quantity,
          piecesProduced,
        });
      });
      if (left <= 0) break;
    }

    if (payload.length === 0) return false;

    try {
      const created = await this.scheduleAssignmentsService.create(payload);
      this.absorbAssignments(Array.isArray(created) ? created : [created]);
      await this.syncItemPlatform(itemId);
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to schedule mark");
      return false;
    }
  };

  // Put ONE piece of a mark on a day, in one form. The seed gesture.
  //
  // Item 3 of the 08-26 plan splits what a drop means. It used to auto-populate
  // the mark's whole remaining quantity down a single form, which is the
  // behaviour Creede asked to change: "it just fleshes it out for 10 days in
  // F1... I can't even come back and pull this back to let it do F1 and F2."
  //
  // Now a drop places one piece and stops, he arranges the day the way the
  // plant will actually pour it -- possibly the same mark in four forms, or two
  // marks side by side in a double -- and `runOutDay` repeats THAT forward.
  // His own description of the rule: "the logic is not far off what you have
  // from once I schedule kind of that first day. I do take that and run that
  // until that mark is done."
  scheduleOnePiece = async (
    itemId: string,
    platform: string,
    dateOnly: string,
    options?: { formId?: string; side?: DoubleSide; quantity?: number }
  ): Promise<boolean> => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return false;

    // A mark pours on its own kind of row. Nothing has ever enforced this --
    // `scheduleCellMap` keys on platform and date alone -- so a VDT mark could
    // be dropped on a precast platform and would render there as though it were
    // scheduled. With the two screens split (2026-09-15) that is no longer a
    // convention worth trusting.
    const rowType = productTypeOfPlatform(platform);
    if (rowType && rowType !== item.productType) {
      this.rootStore.notificationStore?.addNotification({
        message: `${item.markNumber} is a ${item.productType.toUpperCase()} mark, and ${platform} is a ${rowType.toUpperCase()} row. Schedule it on the ${rowType === "vdt" ? "VDT" : "precast"} screen.`,
        options: { variant: "warning" },
      });
      return false;
    }

    if (this.remainingQuantityForItem(itemId) <= 0) {
      this.rootStore.notificationStore?.addNotification({
        message: `${item.markNumber} already has all ${item.quantity} pieces scheduled.`,
        options: { variant: "info" },
      });
      return false;
    }

    // One piece per form per day. Creede, 2026-08-05: "roughly 95% of pours are
    // one piece per form per day", and a double is the exception -- two pieces,
    // one per side.
    //
    // `moveAssignment` has enforced this since it was written; CREATING a
    // placement did not, which item 3 turned from a near-miss into a live hole:
    // a drop used to call scheduleMarkRun, which walked forward across days, so
    // a same-day collision was unlikely. A drop now places one piece on the day
    // you dropped it, so the collision is one click away -- and a mark carrying
    // a pre-assigned formId (Creede's F1A/F1B override) resolves to that form
    // unconditionally, occupied or not.
    //
    // Note what this does NOT forbid: the same mark in SEVERAL forms on one day
    // is the whole point of item 3 -- "I may pour a WAO1 in form one, two,
    // three, and four on Friday this week."
    const formId = options?.formId ?? item.formId ?? null;
    const side = options?.side ?? null;
    const date = toDateOnly(dateOnly);

    // How many pieces this placement carries. One for precast, because that IS
    // the rule; for VDT it is whatever he says, because a mould holds a batch:
    // "I need to be able to drag in multiple pieces from the same form. They may
    // pack all 24 pieces in this form. They may not."
    const asked = Math.floor(options?.quantity ?? 1);
    const remaining = this.remainingQuantityForItem(itemId);
    const quantity = Math.max(1, Math.min(asked || 1, remaining));

    const identity = this.formIdentityForItem(itemId, formId);
    // Truthiness, NOT `!= null`: a formless mark's identity is the empty string,
    // and treating that as an identity makes every formless placement collide
    // with every other one on the day. There is no form to share.
    const inForm = identity
      ? this.scheduleAssignments.filter(
          (a) =>
            a.platform === platform &&
            a.pourDate === date &&
            this.assignmentFormIdentity(a) === identity &&
            // Keyed on side too, so each half of a double takes its own piece.
            (a.side ?? "") === (side ?? "")
        )
      : [];
    // THIS mark's placement first. A VDT mould packs several marks, and taking
    // whichever came first made a drop beside another mark stand a second row
    // for this one instead of topping it up (found in the 09-28 click-through).
    const existing =
      inForm.find((a) => a.productionItemId === itemId) ?? inForm[0];

    // ONE PIECE PER FORM PER DAY -- for precast only. Creede, 2026-08-05:
    // "roughly 95% of pours are one piece per form per day", and a double is the
    // exception: two pieces, one per side.
    //
    // VDT is the other five percent by design, so the rule is gated on product
    // type rather than deleted: a VDT form holding several pieces of one mark on
    // one day is the normal case there, and forbidding it is what made the
    // existing grid unusable for the dry-pack lines.
    //
    // Note what this does NOT forbid for precast either: the same mark in
    // SEVERAL forms on one day is the whole point of item 3 -- "I may pour a
    // WAO1 in form one, two, three, and four on Friday this week."
    //
    // A FULL DOUBLE is the same refusal. The grid hands in no side when
    // freeSideForForm finds neither half open, and a side-less piece matches
    // neither "A" nor "B" above -- so a mark forced into that form (the F1A /
    // F1B override, which skips the pool's capacity count) went in as a third
    // piece. Sided placements in the form mean it is a double with no room.
    const fullDouble =
      !side && identity
        ? this.scheduleAssignments.find(
            (a) =>
              a.platform === platform &&
              a.pourDate === date &&
              this.assignmentFormIdentity(a) === identity &&
              !!a.side
          )
        : undefined;
    if ((existing || fullDouble) && item.productType !== "vdt") {
      const half = side ? ` side ${side}` : "";
      this.rootStore.notificationStore?.addNotification({
        message: `Form ${formId}${half} already has a piece pouring on ${date}. Use another form in the group, or a different day.`,
        options: { variant: "warning" },
      });
      return false;
    }

    // A VDT form already running THIS mark that day takes MORE of it, rather
    // than standing a second row beside the first -- the day sheet prints one
    // row per mark per line, and two rows would read as two separate packs.
    if (existing && item.productType === "vdt") {
      if (existing.productionItemId === itemId) {
        return this.addPiecesToAssignment(existing.id, quantity);
      }
      // A DIFFERENT mark in the same mould on the same day: WARN, don't refuse.
      //
      // This was a refusal until 2026-09-18, on my assumption that a mould packs
      // one shape at a time. Creede's own words name the MOULD, not the mark --
      // "it is critical for our VDT testing that multiple pieces from the same
      // mold can be scheduled on the same day" -- and he is about to test the
      // scheduler in earnest. An assumption of mine standing between him and
      // that test is the worse error: a schedule he did not mean is one drag to
      // undo, while a refusal he did mean costs him the test.
      //
      // Warn rather than block, which is what this app does everywhere a
      // gesture might not be intended -- the same form in two groups of a phase,
      // an over-delivered pallet.
      this.rootStore.notificationStore?.addNotification({
        message: `Form ${formId} is now packing two marks on ${date}. That is allowed — drag one out again if it was not what you meant.`,
        options: { variant: "warning" },
      });
      // Falls through to create a second placement in that form for the day.
    }

    try {
      const created = await this.scheduleAssignmentsService.create({
        productionItemId: itemId,
        formId,
        side,
        platform,
        pourDate: date,
        quantity,
        sequence: this.nextSequence(platform, date),
        // A mark being seeded for the first time may already have poured
        // pieces recorded against it; scheduleMarkRun explains why that has to
        // be carried rather than left at zero. A placement can only carry as
        // many as it schedules.
        piecesProduced:
          this.scheduledQuantityForItem(itemId) === 0
            ? Math.min(quantity, item.piecesProduced || 0)
            : 0,
      });
      this.absorbAssignments(Array.isArray(created) ? created : [created]);
      await this.syncItemPlatform(itemId);
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to schedule the piece");
      return false;
    }
  };

  /**
   * Add pieces to a placement that already exists — VDT's "pack a few more into
   * that mould" gesture.
   *
   * Never exceeds the mark's remaining quantity: the server's own hook caps the
   * sum of a mark's placements against its quantity, so asking for more than is
   * left would fail the whole write rather than partially apply.
   */
  addPiecesToAssignment = async (
    assignmentId: string,
    pieces: number
  ): Promise<boolean> => {
    const assignment = this.scheduleAssignments.find((a) => a.id === assignmentId);
    if (!assignment) return false;

    const itemId = assignment.productionItemId;
    const room = this.remainingQuantityForItem(itemId);
    const added = Math.max(0, Math.min(Math.floor(pieces), room));
    if (added === 0) {
      const item = this.productionItems.find((p) => p.id === itemId);
      this.rootStore.notificationStore?.addNotification({
        message: item
          ? `${item.markNumber} already has all ${item.quantity} pieces scheduled.`
          : "Every piece of that mark is already scheduled.",
        options: { variant: "info" },
      });
      return false;
    }

    try {
      const patched = await this.scheduleAssignmentsService.patch(assignmentId, {
        quantity: (assignment.quantity ?? 0) + added,
      });
      this.absorbAssignments(Array.isArray(patched) ? patched : [patched]);
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to add pieces to that form");
      return false;
    }
  };

  // Take a day exactly as it is arranged and repeat it forward until every mark
  // on it runs out.
  //
  // This is the other half of item 3, and the reason the fill rule is now
  // expressible at all. Creede spreads a mark across every interchangeable form
  // in its group and picks up a new mark as each form empties: "WA10 in forms
  // two through five and A07 in F6... once the first day is set, run WA10 out
  // across all four forms until the quantity is exhausted."
  //
  // Each mark keeps the forms IT occupies on the seed day, so a day holding
  // WA10 in four forms and A07 in a fifth runs WA10 four-a-day and A07
  // one-a-day, finishing on different days -- which is exactly what happens on
  // the floor. Doubles need no special case: a double seeded with the same mark
  // on both sides simply has that mark in two (form, side) slots.
  /**
   * Repeat a day forward until every mark in the seed is fully scheduled.
   *
   * THE CORE. Every run-out mode differs only in which placements seed it, so
   * they all come through here -- the day walking, the one-piece-per-form-per-
   * day rule and the taken-slot skip have exactly one implementation.
   *
   * Each mark keeps the slots it occupies on the seed day, so a day holding
   * WA10 in four forms and A07 in a fifth runs WA10 four a day and A07 one a
   * day. Creede: "once I schedule kind of that first day, I do take that and
   * run that until that mark is done. That is pretty much standard."
   *
   * A slot carries its own PLATFORM. That is what makes a whole-job run-out
   * possible: this used to take the platform as an argument and stamp it on
   * every row it created, which confined the whole operation to one platform
   * row -- the mismatch with how Creede actually works, since he schedules day
   * one across every platform a job is on and then runs the lot out.
   */
  private runOutFrom = async (
    seed: MMScheduleAssignment[],
    dateOnly: string,
    skipSaturdays: boolean
  ): Promise<number> => {
    const date = toDateOnly(dateOnly);
    if (seed.length === 0) return 0;

    type RunOutSlot = {
      formId: string | null;
      side: DoubleSide | null;
      platform: string;
    };

    // Which (platform, form, side) slots each mark holds on the seed day.
    const slotsByItem = new Map<string, RunOutSlot[]>();
    for (const assignment of seed) {
      const slots = slotsByItem.get(assignment.productionItemId) || [];
      slots.push({
        formId: assignment.formId ?? null,
        side: assignment.side ?? null,
        platform: assignment.platform ?? "",
      });
      slotsByItem.set(assignment.productionItemId, slots);
    }

    let scheduled = 0;
    for (const [itemId, slots] of slotsByItem) {
      const remaining = this.remainingQuantityForItem(itemId);
      if (remaining <= 0) continue;

      // Days AFTER the seed day: the seed day is already poured-in.
      //
      // The budget is the days the run needs PLUS room to step over taken
      // slots. It used to be exactly the days needed, so every slot skipped
      // below cost a piece that was never placed: a mark of 8 with one day's
      // form taken by another mark ran out to 7 and said nothing, and a second
      // press found its own placements in the way and reported nothing to do.
      // shortcut: a fixed allowance, so a form booked solid for more than
      // RUN_OUT_SKIP_ALLOWANCE pour days still stops short -- walk day by day
      // if a job ever needs to queue behind a longer block than that.
      const days = pourDayRun(
        date,
        Math.ceil(remaining / slots.length) + 1 + RUN_OUT_SKIP_ALLOWANCE,
        skipSaturdays,
        this.closedDayKeys
      ).slice(1);

      let left = remaining;
      const payload: any[] = [];
      for (const pourDate of days) {
        for (const slot of slots) {
          if (left <= 0) break;
          // One piece per form per day, still. A slot already taken on that day
          // -- by another job sharing the platform, or by a hand adjustment --
          // is skipped rather than double-booked.
          // Keyed by (phase, form), not form number: a different phase's F1
          // is different steel and does not occupy this one's slot.
          const slotIdentity = this.formIdentityForItem(itemId, slot.formId);
          const taken = this.scheduleAssignments.some(
            (a) =>
              a.platform === slot.platform &&
              a.pourDate === pourDate &&
              this.assignmentFormIdentity(a) === slotIdentity &&
              (a.side ?? "") === (slot.side ?? "")
          );
          if (taken) continue;
          left -= 1;
          payload.push({
            productionItemId: itemId,
            formId: slot.formId,
            side: slot.side,
            platform: slot.platform,
            pourDate,
            quantity: 1,
            piecesProduced: 0,
          });
        }
        if (left <= 0) break;
      }

      if (payload.length === 0) continue;
      try {
        const created = await this.scheduleAssignmentsService.create(payload);
        this.absorbAssignments(Array.isArray(created) ? created : [created]);
        scheduled += payload.length;
        // Platform is derived from the schedule, and a run-out can now place a
        // mark on a platform it was not on before. Without this the mark's own
        // platform field goes stale -- scheduleOnePiece and moveAssignment both
        // sync it, and this was the one write path that did not.
        await this.syncItemPlatform(itemId);
      } catch (error) {
        // Per mark, not per operation: one mark overflowing its quantity must
        // not silently discard the run-out of every other mark on the day.
        this.reportScheduleError(error, "Failed to run the day out");
      }
    }

    return scheduled;
  };

  /**
   * Run out one platform-day. The original gesture, unchanged in effect.
   *
   * `projectId` narrows the seed to one job, which is what lets the button work
   * while a project filter is on. It used to be DISABLED under a filter,
   * because an unscoped seed sweeps in other jobs sharing the platform and he
   * could not see what he was about to move.
   */
  runOutDay = async (
    platform: string,
    dateOnly: string,
    skipSaturdays = true,
    projectId?: string
  ): Promise<number> => {
    const date = toDateOnly(dateOnly);
    const seed = this.scheduleAssignments.filter(
      (a) =>
        a.platform === platform &&
        a.pourDate === date &&
        (!projectId || this.projectIdForItem(a.productionItemId) === projectId)
    );
    return this.runOutFrom(seed, date, skipSaturdays);
  };

  /**
   * Run out ONE form on a platform-day -- the narrower of the two cases Creede
   * named: "either running out a specific form or running out everything
   * currently scheduled for the project."
   */
  runOutForm = async (
    platform: string,
    dateOnly: string,
    formId: string | null | undefined,
    skipSaturdays = true
  ): Promise<number> => {
    const date = toDateOnly(dateOnly);
    const wanted = formKey(formId);
    const seed = this.scheduleAssignments.filter(
      (a) =>
        a.platform === platform &&
        a.pourDate === date &&
        formKey(a.formId) === wanted
    );
    return this.runOutFrom(seed, date, skipSaturdays);
  };

  /**
   * Run out a whole JOB from a day, across every platform it sits on.
   *
   * Creede's PRIMARY case, in his own words: "typically, I schedule the first
   * day of a job and then use Run Out to schedule all remaining days across all
   * platforms assigned to that project. Because of that, I would expect the Run
   * Out function to operate across all active forms within the project."
   *
   * Scoped to the ONE project, deliberately not its paired sibling. A paired
   * VDT job is a separate job on separate lines, and pulling it in would run
   * out dry-pack lines from a precast button -- against his own "I separate
   * them out completely" on 2026-09-04.
   */
  runOutProject = async (
    projectId: string,
    dateOnly: string,
    skipSaturdays = true
  ): Promise<number> => {
    const date = toDateOnly(dateOnly);
    const seed = this.scheduleAssignments.filter(
      (a) =>
        a.pourDate === date &&
        this.projectIdForItem(a.productionItemId) === projectId
    );
    return this.runOutFrom(seed, date, skipSaturdays);
  };

  /** The job a placement belongs to, via its mark. */
  private projectIdForItem = (itemId: string): string | undefined =>
    this.productionItems.find((p) => p.id === itemId)?.projectId;

  // Move a placement to another platform/day — dragging a chip between
  // cells. Deliberately not a re-run: hand adjustment after auto-population is
  // the whole point.
  // One VDT placement folded into another of the same mark, form and day --
  // the pieces move, then the emptied placement goes. Pieces already poured
  // stay where they were recorded rather than being merged away.
  private mergeAssignmentInto = async (
    moving: MMScheduleAssignment,
    into: MMScheduleAssignment
  ): Promise<boolean> => {
    if (moving.piecesProduced > 0) {
      this.rootStore.notificationStore?.addNotification({
        message: "That placement already has pieces recorded as poured, so it stays where it is.",
        options: { variant: "warning" },
      });
      return false;
    }
    // REMOVE FIRST, then grow the other. The server caps a mark's scheduled
    // pieces at its quantity, and growing first counts the moving pieces twice:
    // B-02 is 12 pieces, 6 + 6 scheduled, and "patch to 12" while the other 6
    // still exists reads as 18 and is refused.
    try {
      await this.scheduleAssignmentsService.remove(moving.id);
    } catch (error) {
      this.reportScheduleError(error, "Failed to move the scheduled pieces");
      return false;
    }
    runInAction(() => {
      this.scheduleAssignments = this.scheduleAssignments.filter(
        (a) => a.id !== moving.id
      );
    });
    try {
      const patched = await this.scheduleAssignmentsService.patch(into.id, {
        quantity: (into.quantity ?? 0) + (moving.quantity ?? 0),
      });
      this.absorbAssignments(Array.isArray(patched) ? patched : [patched]);
      // A merge removes a placement, and the one removed may have been the
      // mark's earliest -- the placement its platform is read from. This was
      // the one move path that returned before moveAssignment's own sync.
      await this.syncItemPlatform(moving.productionItemId);
      return true;
    } catch (error) {
      // Put the moved pieces back where they were, so a failed merge loses
      // nothing from the schedule.
      try {
        const restored = await this.scheduleAssignmentsService.create({
          productionItemId: moving.productionItemId,
          formId: moving.formId ?? null,
          side: moving.side ?? null,
          platform: moving.platform,
          pourDate: moving.pourDate,
          quantity: moving.quantity,
          piecesProduced: 0,
        });
        this.absorbAssignments(Array.isArray(restored) ? restored : [restored]);
      } catch {
        /* the error below already says the move failed */
      }
      this.reportScheduleError(error, "Failed to move the scheduled pieces");
      return false;
    }
  };

  moveAssignment = async (
    assignmentId: string,
    platform: string,
    dateOnly: string
  ): Promise<boolean> => {
    const target = toDateOnly(dateOnly);

    // ONE PIECE PER FORM PER DAY. This rule has never existed anywhere in the
    // codebase — not in the store, not in a hook, not as a unique index. It was
    // only ever an accident of scheduleMarkRun laying pieces on consecutive
    // days, which is why Creede could reproduce a double-booking live: "you see
    // how now one, two, and three are all double scheduled."
    //
    // Enforced here rather than only in shiftDay because a plain chip drag
    // reaches this same method and had the identical hole.
    //
    // Formless placements opt out (see formKey) — they are not known to share a
    // form with anything, so there is nothing to collide with.
    const moving = this.scheduleAssignments.find((a) => a.id === assignmentId);
    const key = moving ? this.assignmentFormIdentity(moving) : "";
    if (key) {
      // Keyed on SIDE as well as form, because a double form legitimately pours
      // two pieces on one day — one per half. Without the side term the second
      // half of every double would read as a double-booking and be refused.
      // NULL side (an ordinary single pour) collides only with other NULLs.
      const inForm = this.scheduleAssignments.filter(
        (a) =>
          a.id !== assignmentId &&
          a.platform === platform &&
          a.pourDate === target &&
          this.assignmentFormIdentity(a) === key &&
          (a.side ?? "") === (moving?.side ?? "")
      );
      // The same mark first, for the same reason as scheduleOnePiece: with B-01
      // already in D1 that day, a drag of B-02 onto B-02 found B-01, read as
      // "another mark joining the mould", and stood a second B-02 chip beside
      // the first (Sherman's 09-28 click-through).
      const clash =
        inForm.find((a) => a.productionItemId === moving?.productionItemId) ??
        inForm[0];
      const movingItem = this.productionItems.find(
        (p) => p.id === moving?.productionItemId
      );
      if (clash && moving && movingItem?.productType === "vdt") {
        // VDT is the inverse rule (Creede, 2026-09-23): "line one will get form
        // D1 and they will pack various marks in that one form just on line
        // one." The same as scheduleOnePiece, so a drag and a drop agree --
        // they did not, which is why D5 could not be dragged to the next day.
        if (clash.productionItemId === moving.productionItemId) {
          // The same mark already packing that mould that day: fold this
          // placement into it, as a drop would have topped it up.
          return this.mergeAssignmentInto(moving, clash);
        }
        this.rootStore.notificationStore?.addNotification({
          message: `Form ${moving.formId} is now packing two marks on ${target}. That is allowed — drag one out again if it was not what you meant.`,
          options: { variant: "warning" },
        });
      } else if (clash) {
        const half = moving?.side ? ` side ${moving.side}` : "";
        this.rootStore.notificationStore?.addNotification({
          message: `Form ${moving?.formId}${half} already has a piece pouring on ${target}. Shift that day instead of stacking two pieces in one form.`,
          options: { variant: "warning" },
        });
        return false;
      }
    }

    try {
      // Appended where it was dropped -- including onto its own day, which is
      // how a chip is sent to the back of the day's order.
      const patched = await this.scheduleAssignmentsService.patch(
        assignmentId,
        { platform, pourDate: target, sequence: this.nextSequence(platform, target) }
      );
      this.absorbAssignments([patched]);
      // Dragging a chip to another platform row is a platform change like any
      // other, so the mark follows it.
      if (moving) await this.syncItemPlatform(moving.productionItemId);
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to move the scheduled piece");
      return false;
    }
  };

  // Create many marks in ONE request. The backend already accepts array create
  // (mm-production-items sets multi: ['create','patch','remove']), so a
  // 400-mark paste is one round trip rather than 400 -- which also keeps it well
  // clear of the API's 100-req/min rate limiter.
  //
  // Derived fields are computed here exactly as createProductionItem does them,
  // rather than being left to the server: totalCubicFeet and lbsPerPiece have
  // always been client-computed for this entity.
  // Read marks off an uploaded ticket PDF.
  //
  // Returns the SAME {rows, errors} shape parseMarkPaste returns, so the review
  // grid, the duplicate check and createProductionItemsBulk are reused verbatim
  // and the pasted path and the read path cannot drift apart.
  //
  // A FEW PAGES AT A TIME (item 14). One page was one request, strictly in
  // order, which put a 51-page packet at about six and a half minutes -- "a
  // little over 10 seconds a page", which Creede measured against his own ~10
  // seconds a page in Excel.
  //
  // Sherman offered the batch and Creede solved its one risk in the room: run
  // everything, then check for duplicates at the end. That already IS the
  // design -- `mergeContinuationRows` has always run after the whole loop, over
  // `markKey`-folded rows, so it never depended on page order -- and the
  // property that makes it safe is his: marks do not repeat within a packet.
  //
  // THREE, not twenty. The API's rate limiter allows 100 requests a minute
  // (enterpriseSecurity.ts) and his 51-page run was about nine a minute, so
  // three is far inside it while still cutting the wall clock to roughly a
  // third. Failure isolation survives: a page that throws costs that page, not
  // its batch, because each request keeps its own catch.
  //
  // THE ONE HONEST COST: `knownMarks` can only carry pages that have FINISHED,
  // so marks found inside the current batch are not named back to the model
  // while that batch is in flight. The extractor's own comment calls that
  // prompt hint "the belt" and the post-run merge "the braces" -- the braces
  // still hold, which is why this is safe rather than merely faster.
  extractMarksFromTicketPdf = async (
    /**
     * The pages to read: an open PDF that renders a page on request (the real
     * import -- see openTicketPdf), or pages already rendered (the checks).
     */
    pages: TicketPageImage[] | Pick<TicketPdfSource, "pageCount" | "renderPage">,
    onProgress?: (fromPage: number, pageCount: number, toPage?: number) => void,
    /**
     * What the job being imported into is. Sent so the reader can warn when a
     * ticket's top corner says the other thing -- item 16, and the reason it
     * could not warn before: the service was given the page and nothing about
     * the job it was being read for.
     */
    jobProductType?: MarkProductType,
    /** Test seams: the per-page limit, so a lost reply can be exercised, and
        the pause before the end-of-run re-read. */
    options?: { timeoutMs?: number; retryPauseMs?: number }
  ): Promise<MarkPasteResult> => {
    const rows: ParsedMarkRow[] = [];

    // Cause -> the pages it happened on, in first-seen order. THE fix for
    // Creede's "anytime there's more than five warnings, it just says and more":
    // sixty pages failing for one reason is now one entry with sixty pages on
    // it, not sixty entries of which five are visible.
    const byCause = new Map<string, GroupedMessage>();
    const note = (message: TicketExtractMessage, pageNumber?: number) => {
      const existing = byCause.get(message.causeKey);
      if (existing) {
        if (pageNumber != null && !existing.pages.includes(pageNumber)) {
          existing.pages.push(pageNumber);
        }
        return;
      }
      byCause.set(message.causeKey, {
        ...message,
        pages: pageNumber != null ? [pageNumber] : [],
      });
    };

    // Pages read at once. Named rather than inlined so the one number that
    // trades speed against the prompt hint is findable.
    const PAGES_PER_BATCH = 3;

    /**
     * How long one page may take before it is given up on.
     *
     * There was NO limit until 2026-09-18, and Creede's import froze about
     * three quarters of the way through a packet on production. A page is one
     * socket call, and a call whose reply never arrives -- a hiccup, a container
     * recycling, a dropped ack -- leaves a promise that never settles; the batch
     * waiting on it never resolves and the whole import stops, with no error and
     * no way forward.
     *
     * A page takes around ten seconds, so two minutes is far past slow and only
     * catches genuinely lost work. A page given up on is reported and the rest of
     * the packet still finishes.
     */
    const timeout = options?.timeoutMs ?? 120_000;

    // Pages that hit a rate limit or a time limit, held back for ONE more read
    // after the last batch (2026-10-16 item 32: "no more pages given up at the
    // start of a run"). Their message is only reported if that read fails too.
    const retry = new Map<number, TicketExtractMessage>();
    const isRetryable = (text: string) => /rate-limiting|took longer than/i.test(text);

    const readPage = async (page: TicketPageImage, known: string[], lastTry = false) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const result = await Promise.race([
          this.ticketExtractService.create({
          image: page.image,
          mimeType: page.mimeType,
          jobProductType,
          pageNumber: page.pageNumber,
          printedValues: page.printedValues,
          // The page's printed LINES, rebuilt from its text layer. Never sent
          // until 2026-10-01: the rasteriser built them and the API knew how to
          // use them, but this call dropped them, so every page was read from
          // pixels alone. Exact where a packet has a text layer.
          printedRows: page.printedRows,
          // The table crop (10-07 item 3); ignored unless the server's switch
          // is on.
          tableImage: page.tableImage,
          tableMimeType: page.tableMimeType,
          // What the FINISHED pages already found. Each page is its own vision
          // call, so without this page 2 has no way to know that the mark named
          // at the top of it is the one page 1 was about. Pages in the same
          // batch cannot see each other; mergeContinuationRows catches those.
            knownMarks: known,
          }),
          new Promise<never>((_resolve, reject) => {
            timer = setTimeout(
              () =>
                reject(
                  new Error(
                    `took longer than ${Math.round(timeout / 1000)}s and was given up on`
                  )
                ),
              timeout
            );
          }),
        ]);

        // A version bump means the server is ahead of this client. Say so
        // rather than reading fields that may have moved.
        if (result?.contractVersion !== MM_TICKET_EXTRACT_CONTRACT_VERSION) {
          note(
            {
              kind: "error",
              causeKey: "client:contract-skew",
              text: "This app is out of date with the server and cannot read the result. Reload the page.",
            },
            page.pageNumber
          );
          return;
        }

        readFrom[result.reader?.readFrom === "text" ? "text" : "image"].push(
          page.pageNumber
        );

        if (!lastTry && result.ok === false && isRetryable(result.error ?? "")) {
          retry.set(page.pageNumber, result.messages?.[0] ?? {
            kind: "error",
            causeKey: `fail:${result.error}`,
            text: result.error ?? "could not be read",
          });
          return;
        }

        for (const row of result.rows ?? []) rows.push(row);
        for (const message of result.messages ?? []) note(message, page.pageNumber);
      } catch (error) {
        // Never abandon the run: pages 1-9 reading cleanly is worth keeping
        // even when page 10 fails.
        const text = mmErrorMessage(error, "could not be read");
        const message: TicketExtractMessage = { kind: "error", causeKey: `client:${text}`, text };
        if (!lastTry && isRetryable(text)) {
          retry.set(page.pageNumber, message);
          return;
        }
        note(message, page.pageNumber);
      } finally {
        if (timer !== undefined) clearTimeout(timer);
        // RELEASE THE IMAGE. Each page holds up to two million base64
        // characters -- about 4 MB in memory once the string is UTF-16. Pages
        // are now rendered a batch at a time, so this is what keeps the peak at
        // a couple of batches rather than creeping up across a long packet.
        // Nothing downstream needs the picture once the page has been read.
        page.image = "";
        page.tableImage = undefined;
      }
    };

    // Where each page's numbers came from. A page read from the file's own text
    // layer is EXACT; one read from the image is a visual reading that the
    // cross-checks can question but not guarantee. Saying which is which is the
    // honest summary of an import, and it is also how a half-deployed pair shows
    // itself -- the printed lines are sent by THIS half, so if none of the pages
    // report `text` on a packet that has a text layer, this build is not the one
    // talking to the API.
    const readFrom: Record<"text" | "image", number[]> = { text: [], image: [] };

    // RENDERED A BATCH AT A TIME (2026-09-23), not all up front. Rendering the
    // whole packet first is what forced the sixty-page cap: three hundred pages
    // at ~4 MB each is over a gigabyte before a single page is read. The next
    // batch renders while this one is being read, so the network wait hides the
    // rendering and peak memory is two batches, whatever the packet's length.
    const source = Array.isArray(pages)
      ? {
          pageCount: pages.length,
          renderPage: async (n: number) => pages[n - 1] ?? null,
        }
      : pages;
    const pageCount = source.pageCount;

    // One page at a time: an open PDF renders every page on one canvas. A page
    // that cannot be drawn is named, rather than vanishing from the count.
    const renderBatch = async (from: number): Promise<TicketPageImage[]> => {
      const out: TicketPageImage[] = [];
      const to = Math.min(pageCount, from + PAGES_PER_BATCH - 1);
      for (let n = from; n <= to; n++) {
        try {
          const rendered = await source.renderPage(n);
          if (rendered) {
            out.push(rendered);
            continue;
          }
        } catch {
          /* reported below, same as a page that rendered to nothing */
        }
        note(
          {
            kind: "error",
            causeKey: "raster:unrenderable",
            text: "This page could not be turned into an image, so it was not read.",
          },
          n
        );
      }
      return out;
    };

    let upcoming = pageCount > 0 ? renderBatch(1) : Promise.resolve([]);
    for (let from = 1; from <= pageCount; from += PAGES_PER_BATCH) {
      const batch = await upcoming;
      const next = from + PAGES_PER_BATCH;
      upcoming = next <= pageCount ? renderBatch(next) : Promise.resolve([]);
      if (batch.length === 0) continue;
      onProgress?.(
        batch[0].pageNumber,
        pageCount,
        batch[batch.length - 1].pageNumber
      );
      // Snapshotted BEFORE the batch fires, so every page in it gets the same
      // list and no page sees a half-written one.
      const known = rows.map((r) => r.markNumber);
      // Rows are pushed as each page resolves, so a batch's rows can interleave.
      // Order is restored by the sort below rather than by the read order.
      await Promise.all(batch.map((page) => readPage(page, known)));
    }

    // The held-back pages, once, after a short pause for the limit to clear.
    // One at a time: they failed for being too many at once. A page that
    // fails again is reported as it would have been.
    if (retry.size > 0) {
      await new Promise((resolve) => setTimeout(resolve, options?.retryPauseMs ?? 5_000));
      for (const [pageNumber, firstFailure] of [...retry].sort(([a], [b]) => a - b)) {
        onProgress?.(pageNumber, pageCount);
        let page: TicketPageImage | null = null;
        try {
          page = await source.renderPage(pageNumber);
        } catch {
          page = null;
        }
        if (!page) {
          note(firstFailure, pageNumber);
          continue;
        }
        await readPage(page, rows.map((r) => r.markNumber), true);
      }
    }

    // Back into page order. The grid, the duplicate prompt and the write all
    // read this list, and a packet whose marks came back in a jumbled order
    // would be unreviewable against the PDF in his hand.
    rows.sort((a, b) => (a.sourcePage ?? 0) - (b.sourcePage ?? 0));

    if (readFrom.text.length > 0) {
      note({
        kind: "info",
        causeKey: "reader:read-from-text",
        text: `${readFrom.text.length} page(s) were read from the file's own text, so those numbers are exact: ${formatPageRange(readFrom.text)}.`,
      });
    }
    if (readFrom.image.length > 0 && readFrom.text.length > 0) {
      note({
        kind: "info",
        causeKey: "reader:read-from-image",
        text: `${readFrom.image.length} page(s) had no text to read and were read from the drawing: ${formatPageRange(readFrom.image)}. Those are the ones worth checking.`,
      });
    }

    // Fold continuation pages together BEFORE anything is reported, so the
    // counts, the review grid and the write all see one row per mark.
    const merged = mergeContinuationRows(rows);
    for (const text of merged.errors) {
      note({ kind: "error", causeKey: `merge:${text}`, text });
    }

    if (merged.rows.length === 0 && byCause.size === 0) {
      note({
        kind: "error",
        causeKey: "batch:nothing-found",
        text: "No marks were found in this PDF. Check it is a production ticket rather than a drawing or cover sheet.",
      });
    }

    const messages = [...byCause.values()];
    return {
      rows: merged.rows,
      // `errors` stays populated so anything still reading the flat list keeps
      // working; the dialog reads `messages`.
      errors: messages.filter((m) => m.kind === "error").map((m) => m.text),
      continued: merged.continued,
      messages,
    };
  };

  createProductionItemsBulk = async (
    rows: ParsedMarkRow[],
    common: {
      projectId: string;
      ticketGroupId: string;
      productType: MarkProductType;
      platform: string;
    }
  ): Promise<number> => {
    if (rows.length === 0) return 0;

    const payload = rows.map((row) => ({
      projectId: common.projectId,
      ticketGroupId: common.ticketGroupId,
      markNumber: row.markNumber,
      productType: common.productType,
      quantity: row.quantity,
      cubicFeetPerPiece: row.cubicFeetPerPiece,
      totalCubicFeet:
        Math.round(row.quantity * row.cubicFeetPerPiece * 100) / 100,
      lbsPerPiece: Math.round(
        row.cubicFeetPerPiece * lbsPerCuFt(common.productType)
      ),
      platform: common.platform,
      qtyPerForm: 1,
      isDouble: false,
      piecesProduced: 0,
      piecesDelivered: 0,
      percentProduced: 0,
      percentDelivered: 0,
      // THE READER'S DOUBT, kept. This is where it used to die: the payload was
      // built from the three value fields only, so confidence and the source
      // pages were dropped on the floor and the mark landed in staging carrying
      // neither. Creede reported it as the check "didn't appear" on a re-scan --
      // structurally it could not, once written.
      //
      // Rounded to two places to match the DECIMAL(3,2) column exactly, so what
      // comes back compares against the threshold the same way it went in.
      reviewConfidence:
        typeof row.confidence === "number"
          ? Math.round(row.confidence * 100) / 100
          : undefined,
      sourcePages: pageLabelFor(row),
      // THE TICKET'S OWN DATE, read off the page it was printed on
      // (2026-09-15). Stored per mark so a mark added to a group later carries
      // the date of ITS ticket while the group stays whole. Undefined when the
      // page printed no date, or when the reader could not agree with itself
      // about it -- an invented date would be worse than none.
      ticketDate: row.ticketDate,
      // No formId on purpose. The form is chosen when the mark is DRAGGED onto
      // the schedule now, not when it is created -- these arrive unassigned and
      // land in their form group's pool.
    }));

    try {
      const created: any = await this.productionItemsService.create(payload);
      const list = Array.isArray(created) ? created : [created];

      // Built from the payload plus the returned id, the way
      // createProductionItem does it — the row mapper lives inline in loadData
      // and extracting it is a bigger change than this needs. Paired by index:
      // a batch insert returns rows in the order it was given them.
      runInAction(() => {
        list.forEach((raw: any, index: number) => {
          const source = payload[index];
          if (!source) return;
          this.productionItems.push({
            ...source,
            id: raw?.productionItemId ?? raw?.id,
            formId: undefined,
          } as MMProductionItem);
        });
      });
      return list.length;
    } catch (error) {
      this.reportScheduleError(error, "Failed to add the pasted marks");
      return 0;
    }
  };

  // The closed days as a plain set, which is what the pour-day helpers take.
  // Plant-wide closures only: a platform-scoped closure shuts one line, and the
  // day walkers reason about the calendar rather than about a platform.
  get closedDayKeys(): ClosedDays {
    return new Set(
      this.scheduleClosures
        .filter((closure) => !closure.platform)
        .map((closure) => closure.closureDate)
    );
  }

  isDayClosed = (dateOnly: string, platform?: string): boolean =>
    this.scheduleClosures.some(
      (closure) =>
        closure.closureDate === toDateOnly(dateOnly) &&
        (!closure.platform || closure.platform === platform)
    );

  closureFor = (dateOnly: string): MMScheduleClosure | undefined =>
    this.scheduleClosures.find(
      (closure) => closure.closureDate === toDateOnly(dateOnly)
    );

  // Close a day, and push what was scheduled on it forward.
  //
  // Creede asked for both in one gesture. On what happens to work already
  // scheduled that day: "Yeah. Yes, please." The push reuses shiftDay rather
  // than walking the placements here, so the one-piece-per-form-per-day rule is
  // enforced by the same code that enforces it everywhere else — a second
  // implementation of that invariant is how the two drift apart.
  closeDay = async (
    dateOnly: string,
    reason?: string,
    skipSaturdays = false
  ): Promise<boolean> => {
    const date = toDateOnly(dateOnly);
    if (!date) return false;

    try {
      const created: any = await this.scheduleClosuresService.create({
        closureDate: date,
        reason: reason?.trim() || null,
        platform: null,
        active: true,
      });
      runInAction(() => {
        this.scheduleClosures.push({
          id: created?.closureId ?? created?.id,
          closureDate: date,
          reason: reason?.trim() || undefined,
        });
      });
    } catch (error) {
      this.reportScheduleError(error, "Failed to close the day");
      return false;
    }

    // Push every platform that had unpoured work. shiftDay is a no-op on a
    // platform with nothing to move, so this is safe to run across all of them.
    let ok = true;
    for (const row of SCHEDULE_ROWS) {
      const hasWork = this.getScheduleCellPlacements(row.platform, date).some(
        ({ assignment }) => assignment.piecesProduced < assignment.quantity
      );
      if (hasWork)
        ok = (await this.shiftDay(row.platform, date, skipSaturdays)) && ok;
    }
    return ok;
  };

  // Reopening leaves the schedule alone. Work pushed off a closed day was moved
  // deliberately and may since have been rearranged by hand; silently pulling it
  // back would undo decisions nobody asked to undo.
  reopenDay = async (dateOnly: string): Promise<boolean> => {
    const date = toDateOnly(dateOnly);
    const closure = this.scheduleClosures.find((c) => c.closureDate === date);
    if (!closure) return true;
    try {
      await this.scheduleClosuresService.remove(closure.id);
      runInAction(() => {
        this.scheduleClosures = this.scheduleClosures.filter(
          (c) => c.id !== closure.id
        );
      });
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to reopen the day");
      return false;
    }
  };

  // ==========================================================================
  // FORM GROUPS
  //
  // "Form group" is Creede's word for what this app calls a TICKET GROUP. He
  // defined it on 2026-08-14: "my form group one is gonna be one, even if it's
  // one through 10, form group two is gonna be 11 through 15" — a named run of
  // forms within a job — and described creating one as "when I make a form
  // group... those go in form group one."
  //
  // mmTicketGroups already models exactly that: a name, a `formNumbers` pool,
  // and the marks that belong to it. Nothing new had to be built.
  // ==========================================================================

  // The forms a mark in this group may pour in.
  //
  // Falls back to the job's active forms when the group has no pool, which is
  // not an edge case: 12 of the client's 23 ticket groups have an empty
  // `formNumbers` today. Without the fallback the schedule panel would offer no
  // forms at all for over half of them.
  formPoolForGroup = (ticketGroupId?: string): string[] => {
    const group = ticketGroupId
      ? this.ticketGroups.find((g) => g.id === ticketGroupId)
      : undefined;
    const pool = (group?.formNumbers ?? []).filter(
      (formNumber) => !!formKey(formNumber)
    );
    if (pool.length > 0) return [...pool].sort(compareNaturally);

    const projectId = group?.projectId ?? this.selectedProjectId ?? "";
    return this.getFormsByProject(projectId)
      .map((form) => form.formNumber)
      .filter((formNumber) => !!formKey(formNumber))
      .sort(compareNaturally);
  };

  // How Creede reads a form group once pieces are in the yard: as a RANGE.
  // "If form group one is one and one A, just put F1 through F1A... or F1 if
  // it's through 10, F1 dash F10, because I know then it's F1 through F10."
  //
  // No "Form" prefix — "putting 'form' here almost is repetitive. That's what
  // the F means."
  formGroupLabelForMark = (projectId: string, markNumber: string): string => {
    const target = markNumber.trim().toUpperCase();
    const mark = this.productionItems.find(
      (item) =>
        item.projectId === projectId &&
        item.markNumber.trim().toUpperCase() === target
    );
    if (!mark) return "";
    return formRange(this.formPoolForGroup(mark.ticketGroupId));
  };

  // Is this form number flagged as a double on this job? Form numbers repeat
  // across jobs, so the lookup is keyed by both.
  // `phase` is REQUIRED rather than optional on purpose. Defaulting it is the
  // bug: flagging phase 2's F1 as a double made phase 1's F1 read as one too,
  // which raised its scheduling capacity from one piece to two and let a second
  // mark land in a single form. Pass phaseOfItem(mark.id), or "" for a job with
  // no phases.
  isDoubleFormNumber = (
    projectId: string,
    formNumber: string | undefined,
    phase: string
  ): boolean => {
    // Matched with formInPhase rather than by identity: a phased group is
    // allowed to assign the job's unphased steel (getFormsByPhase offers it),
    // and that form has to keep reading as the double it is. An explicitly
    // phased form is still fenced to its own phase, which is the bug this
    // argument exists to stop.
    const key = formKey(formNumber);
    if (!key) return false;
    const candidates = this.forms.filter(
      (form) =>
        form.projectId === projectId &&
        formKey(form.formNumber) === key &&
        formInPhase(form.phase, phase)
    );
    // THE MOST SPECIFIC RECORD WINS. Once a phase has built its own D1, that
    // record answers for D1 in that phase and the job's older unphased D1 is
    // no longer consulted -- they are two pieces of steel that happen to share
    // a number, which is the whole of item 13. Without this the unphased one
    // could hand its isDouble to steel that is not a double, putting a second
    // mark in a single form. Sherman found this clicking through 3963.
    const own = candidates.filter(
      (form) => phaseKey(form.phase) !== "" && phaseKey(form.phase) === phaseKey(phase)
    );
    return (own.length ? own : candidates).some((form) => form.isDouble);
  };

  // The same question asked the way callers actually have it: from a mark.
  isDoubleForItem = (itemId: string, formNumber?: string): boolean => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return false;
    return this.isDoubleFormNumber(
      item.projectId,
      formNumber,
      this.phaseOfItem(itemId)
    );
  };

  // The free half of a double form on a given day, or undefined when both are
  // occupied. "A" is the left side and fills first, so a double scheduled with
  // only one mark behaves exactly like a single pour.
  freeSideForForm = (
    formNumber: string,
    platform: string,
    dateOnly: string,
    phase: string,
    projectId?: string
  ): DoubleSide | undefined => {
    const identity = formIdentity(
      formNumber,
      this.steelPhase(projectId, formNumber, phaseKey(phase))
    );
    const date = toDateOnly(dateOnly);
    const projectOf = (a: MMScheduleAssignment) =>
      this.productionItems.find((p) => p.id === a.productionItemId)?.projectId;
    const used = new Set(
      this.scheduleAssignments
        .filter(
          (a) =>
            a.platform === platform &&
            a.pourDate === date &&
            this.steelIdentityForItem(a.productionItemId, a.formId) === identity &&
            // Another job's form of the same number is different steel.
            (projectId === undefined || projectOf(a) === projectId)
        )
        .map((a) => a.side ?? "A")
    );
    if (!used.has("A")) return "A";
    if (!used.has("B")) return "B";
    return undefined;
  };

  // Which form a mark should pour in, decided at DROP time rather than at mark
  // creation. Creede: "I don't really know what form it's going to go in until
  // I'm over here kind of already scheduling it... the first mark I drag, put it
  // in form one. The next mark I drag... would then be poured in the next form
  // in order in that form group."
  //
  // So: the first form in the group's pool with nothing already pouring on that
  // platform and day. One form serves the mark's whole run, because the run is
  // one piece per day in one form.
  //
  // A mark that already carries a formId keeps it. That is the exception Creede
  // asked to preserve unprompted — "maybe a form in a form group is set up for a
  // return or a termination that the rest of that form group isn't. And I'll
  // pour all my returns in that one form. In [that] case, I can still be able to
  // go ahead and pre-denote."
  resolveFormForDrop = (
    itemId: string,
    platform: string,
    dateOnly: string
  ): string | undefined => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item) return undefined;
    if (formKey(item.formId)) return item.formId; // pre-assigned override

    const date = toDateOnly(dateOnly);
    const onDay = this.scheduleAssignments.filter(
      (a) => a.platform === platform && a.pourDate === date
    );
    // A double form has TWO slots that day, one per side, so it is only full
    // when both are occupied. Counting it as taken after one piece would send
    // the next mark to a different form and lose the pairing Creede is after.
    // Keyed by (phase, form): another phase's F1 pouring the same day is a
    // different form and leaves this one free.
    const usedSlots = new Map<string, number>();
    for (const a of onDay) {
      const key = this.assignmentFormIdentity(a);
      if (key) usedSlots.set(key, (usedSlots.get(key) ?? 0) + 1);
    }

    const phase = this.phaseOfItem(itemId);
    const pool = this.formPoolForGroup(item.ticketGroupId);
    const free = pool.find((formNumber) => {
      const key = this.formIdentityForItem(itemId, formNumber);
      const capacity = this.isDoubleFormNumber(
        item.projectId,
        formNumber,
        phase
      )
        ? 2
        : 1;
      return (usedSlots.get(key) ?? 0) < capacity;
    });
    if (item.productType !== "vdt") return free;

    // VDT never runs out of room (Creede, 2026-09-23): a mould is packed with
    // as many marks as the line puts in it, so an occupied form is not a full
    // one. Prefer the form already running THIS mark on that line and day --
    // the drop then tops it up -- then a free one, then the first in the pool.
    // This used to refuse with "Every form in this group is already pouring",
    // before scheduleOnePiece's VDT rule was ever reached.
    const alreadyPacking = pool.find((formNumber) =>
      onDay.some(
        (a) =>
          a.productionItemId === itemId &&
          this.assignmentFormIdentity(a) === this.formIdentityForItem(itemId, formNumber)
      )
    );
    return alreadyPacking ?? free ?? pool[0];
  };

  // Take one placement off the schedule. Unscheduling REMOVES the row — there
  // is no assignment without a day — which returns its pieces to the mark's
  // remainder automatically.
  removeAssignment = async (assignmentId: string): Promise<boolean> => {
    const assignment = this.scheduleAssignments.find(
      (a) => a.id === assignmentId
    );
    try {
      await this.scheduleAssignmentsService.remove(assignmentId);
      runInAction(() => {
        const index = this.scheduleAssignments.findIndex(
          (a) => a.id === assignmentId
        );
        if (index !== -1) this.scheduleAssignments.splice(index, 1);
      });
      if (assignment) {
        this.syncItemProduced(assignment.productionItemId);
        await this.syncItemPlatform(assignment.productionItemId);
      }
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to unschedule the piece");
      return false;
    }
  };

  // Take a mark off the schedule entirely — every placement of it. This is the
  // drop-onto-the-unscheduled-panel gesture.
  unscheduleMark = async (itemId: string): Promise<boolean> => {
    const assignments = this.assignmentsByItemId.get(itemId) || [];
    if (assignments.length === 0) return true;
    const results = await Promise.all(
      assignments.map((assignment) => this.removeAssignment(assignment.id))
    );
    return results.every(Boolean);
  };

  // Per-day pour log. Writes to the PLACEMENT, not the mark — the server sums
  // them back onto the mark, so a mark poured over three days finally records
  // which pieces landed on which day instead of crediting the whole mark to
  // its completion date.
  // Item 8 — confirm a whole platform-day's pours in one press.
  //
  // Creede: confirming piece by piece is "just a little tedious." His floor
  // process is SUBTRACTIVE — he reviews the day's list, shifts out what is not
  // being poured, then confirms the rest — so this deliberately acts on
  // whatever is still on the day, after the shifting.
  //
  // Precast only, and that is D3, answered by Creede on 2026-09-01: "VDT is our
  // dry pack product line. It's essentially the same situation as Precast and
  // wet cast being two names for the same thing." Both names were already in
  // the system under different words. VDT keeps manual entry because a form is
  // often part-finished there — "I think VDT/dry pack is best left as it
  // currently operates for now."
  //
  // Returns how many placements were confirmed; 0 means there was nothing to do.
  //
  // `projectId` narrows it to one job, for the same reason runOutDay takes one:
  // under a project filter the other jobs sharing the platform are hidden, and
  // a press that confirms pours he cannot see is recording work nobody checked.
  markDayPoured = async (
    platform: string,
    dateOnly: string,
    projectId?: string
  ): Promise<number> => {
    const date = toDateOnly(dateOnly);
    const placements = this.getScheduleCellPlacements(platform, date).filter(
      ({ assignment, item }) =>
        item.productType !== "vdt" &&
        (!projectId || item.projectId === projectId) &&
        assignment.piecesProduced < assignment.quantity
    );
    if (placements.length === 0) return 0;

    // Twenty forms is twenty PATCHes, and the API rate limiter allows 100
    // requests a minute -- already crowded by a single app boot. The service
    // accepts multi-patch, so the common case (every placement pouring its
    // whole quantity) goes out as ONE query-filtered request per quantity
    // group rather than one per placement.
    const byQuantity = new Map<number, string[]>();
    for (const { assignment } of placements) {
      const ids = byQuantity.get(assignment.quantity) ?? [];
      ids.push(assignment.id);
      byQuantity.set(assignment.quantity, ids);
    }

    let confirmed = 0;
    const touchedItems = new Set<string>();

    for (const [quantity, ids] of byQuantity) {
      try {
        const patched = await this.scheduleAssignmentsService.patch(
          null,
          { piecesProduced: quantity },
          { query: { assignmentId: { $in: ids } } }
        );
        this.absorbAssignments(
          Array.isArray(patched) ? patched : [patched]
        );
        confirmed += ids.length;
      } catch (error) {
        // A multi-patch the server refuses must not lose the whole day. Fall
        // back to one at a time for THIS group only, so a single bad row costs
        // one failure rather than nineteen good ones.
        for (const id of ids) {
          const ok = await this.updateAssignmentProduced(id, quantity);
          if (ok) confirmed += 1;
        }
      }
    }

    for (const { assignment } of placements) {
      touchedItems.add(assignment.productionItemId);
    }

    // Once per MARK at the end, not once per placement: a mark in five forms
    // would otherwise re-derive and re-stamp itself five times, which is five
    // more requests than the rate limiter needs to see.
    for (const itemId of touchedItems) {
      this.syncItemProduced(itemId);
      await this.stampCompletionDate(itemId);
    }

    return confirmed;
  };

  updateAssignmentProduced = async (
    assignmentId: string,
    piecesProduced: number
  ): Promise<boolean> => {
    const assignment = this.scheduleAssignments.find(
      (a) => a.id === assignmentId
    );
    if (!assignment) return false;
    // VDT may record MORE than it scheduled (2026-10-07): the crew makes a
    // spare as a hedge -- "he made 25th piece so that if he had any issues with
    // this 24, he could replace it." That is an overage, not a recast: it adds
    // nothing to schedule. Precast keeps its cap.
    const item = this.productionItems.find((p) => p.id === assignment.productionItemId);
    const cap = item?.productType === "vdt" ? Number.MAX_SAFE_INTEGER : assignment.quantity;
    const clamped = Math.max(0, Math.min(Math.floor(piecesProduced) || 0, cap));
    try {
      const patched = await this.scheduleAssignmentsService.patch(
        assignmentId,
        { piecesProduced: clamped }
      );
      this.absorbAssignments([patched]);
      this.syncItemProduced(assignment.productionItemId);
      await this.stampCompletionDate(assignment.productionItemId);
      return true;
    } catch (error) {
      this.reportScheduleError(error, "Failed to record pieces poured");
      return false;
    }
  };

  // Credit a completed mark to the day it was actually poured.
  //
  // The daily report buckets on `mmProductionItems.productionCompletedDate`.
  // The server's assignment sync patches the mark with `piecesProduced` alone,
  // so the stamping resolver fills that column with the API host's UTC day of
  // DATA ENTRY — which is the exact mis-crediting the last cycle fixed and
  // which moving poured entry onto placements would otherwise have brought
  // straight back. The honest date is sitting on the placement.
  //
  // The resolver honours an explicit client value ahead of its own stamp, so
  // sending it here wins. Uses the LATEST pour date that actually has
  // production against it — the day the mark finished.
  private stampCompletionDate = async (itemId: string): Promise<void> => {
    const item = this.productionItems.find((p) => p.id === itemId);
    if (!item || item.quantity <= 0) return;

    const assignments = this.assignmentsByItemId.get(itemId) || [];
    const produced = assignments.reduce((sum, a) => sum + a.piecesProduced, 0);
    // Complete once the replacements are poured too, not when the failed piece
    // itself is counted (09-30 item 9).
    if (produced < item.quantity + (item.piecesExtra ?? 0)) return;

    const completedOn = assignments
      .filter((a) => a.piecesProduced > 0 && a.pourDate)
      .map((a) => a.pourDate)
      .sort()
      .pop();
    if (!completedOn || completedOn === item.productionCompletedDate) return;

    try {
      await this.productionItemsService.patch(itemId, {
        productionCompletedDate: completedOn,
      });
      runInAction(() => {
        const index = this.productionItems.findIndex((p) => p.id === itemId);
        if (index !== -1) {
          this.productionItems[index] = {
            ...this.productionItems[index],
            productionCompletedDate: completedOn,
          };
        }
      });
    } catch (error) {
      // Non-fatal: the pieces are recorded either way. Say so rather than
      // leaving the report quietly crediting the wrong day.
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(
          error,
          "Pieces recorded, but the daily report's date could not be corrected"
        ),
        options: { variant: "warning" },
      });
    }
  };

  // Creede's "I just shift them down a day": every UNPOURED placement in the
  // cell moves to the next pour day; fully-poured ones stay put as the day's
  // record. Saturday is a real pour day here — this is a manual action, not
  // auto-population.
  // Everything that has to move when a day is shifted: the unpoured placements
  // in the clicked cell, plus — for each FORM represented there — every later
  // unpoured placement of that same form on that platform.
  //
  // Creede: "If I'm going to shift a piece in form one Monday... it needs to
  // shift the following form ones that are scheduled back a day as well."
  // Without this the shifted piece simply lands on top of whatever the form was
  // already pouring the next day.
  private shiftChain = (
    platform: string,
    fromDate: string
  ): MMScheduleAssignment[] => {
    const unpoured = this.getScheduleCellPlacements(platform, fromDate).filter(
      ({ assignment }) => assignment.piecesProduced < assignment.quantity
    );
    if (unpoured.length === 0) return [];

    const chain = new Map<string, MMScheduleAssignment>();
    for (const { assignment } of unpoured) {
      chain.set(assignment.id, assignment);

      const key = this.assignmentFormIdentity(assignment);
      if (!key) continue; // formless placements move alone

      for (const later of this.scheduleAssignments) {
        if (
          later.platform === platform &&
          later.pourDate > fromDate &&
          // Same form means same phase too — shifting phase 2's F1 must not
          // drag phase 1's F1 along behind it.
          this.assignmentFormIdentity(later) === key &&
          // Each half of a double runs its own sequence, so shifting one side
          // must not drag the other's pieces along with it.
          (later.side ?? "") === (assignment.side ?? "") &&
          later.piecesProduced < later.quantity
        ) {
          chain.set(later.id, later);
        }
      }
    }
    return [...chain.values()];
  };

  // Push a day's unpoured work to the next pour day, cascading the rest of each
  // form's run with it.
  //
  // Sequential and FURTHEST-DAY-FIRST, not Promise.all. The one-piece-per-form-
  // per-day guard in moveAssignment would reject a piece moving onto a day its
  // form still occupies, so the far end of the run has to vacate before the
  // piece behind it arrives. Parallel moves would race that guard and reject
  // arbitrary members of the chain.
  shiftDay = async (
    platform: string,
    fromDate: string,
    skipSaturdays = false
  ): Promise<boolean> => {
    const from = toDateOnly(fromDate);
    const chain = this.shiftChain(platform, from).sort((a, b) =>
      b.pourDate.localeCompare(a.pourDate)
    );
    if (chain.length === 0) return true;

    let ok = true;
    for (const assignment of chain) {
      const target = nextPourDay(assignment.pourDate, this.closedDayKeys, skipSaturdays);
      const moved = this.isPartlyCastVdt(assignment)
        ? await this.splitRemainderTo(assignment, target)
        : await this.moveAssignment(assignment.id, platform, target);
      ok = moved && ok;
    }
    return ok;
  };

  // The next place in a cell's drag order: one past the highest there.
  private nextSequence = (platform: string, date: string): number =>
    this.scheduleAssignments
      .filter((a) => a.platform === platform && a.pourDate === date)
      .reduce((max, a) => Math.max(max, a.sequence ?? 0), 0) + 1;

  // A VDT placement with SOME of its pieces cast. Creede, 2026-09-30: "say I
  // type in 24 here... even though they made 24, it won't drag that six over,
  // it'll drag all 30. What I'd like for it to do is retain that 24 here and
  // drag the remaining six over." Precast is deliberately not included -- "this
  // functions exactly like I want it. So don't change anything in here."
  private isPartlyCastVdt = (assignment: MMScheduleAssignment): boolean => {
    if (!(assignment.piecesProduced > 0 && assignment.piecesProduced < assignment.quantity)) {
      return false;
    }
    const item = this.productionItems.find((p) => p.id === assignment.productionItemId);
    return item?.productType === "vdt";
  };

  /**
   * Keep what was cast on its day and move only the rest: the placement shrinks
   * to its cast count, and the remainder goes to `target` -- topping up the
   * same mark in the same mould if it is already there, else as a new row.
   *
   * SHRINK FIRST. The server caps a mark's scheduled pieces at its quantity, so
   * creating the remainder before shrinking reads as 36 of 30 and is refused --
   * the same ordering as mergeAssignmentInto.
   */
  private splitRemainderTo = async (
    assignment: MMScheduleAssignment,
    target: string
  ): Promise<boolean> => {
    const cast = assignment.piecesProduced;
    const remainder = assignment.quantity - cast;
    try {
      const shrunk = await this.scheduleAssignmentsService.patch(assignment.id, {
        quantity: cast,
      });
      this.absorbAssignments(Array.isArray(shrunk) ? shrunk : [shrunk]);
    } catch (error) {
      this.reportScheduleError(error, "Failed to shift the uncast pieces");
      return false;
    }

    const identity = this.assignmentFormIdentity(assignment);
    const already = this.scheduleAssignments.find(
      (a) =>
        a.id !== assignment.id &&
        a.productionItemId === assignment.productionItemId &&
        a.platform === assignment.platform &&
        a.pourDate === target &&
        this.assignmentFormIdentity(a) === identity &&
        (a.side ?? "") === (assignment.side ?? "")
    );
    try {
      const written = already
        ? await this.scheduleAssignmentsService.patch(already.id, {
            quantity: already.quantity + remainder,
          })
        : await this.scheduleAssignmentsService.create({
            productionItemId: assignment.productionItemId,
            formId: assignment.formId ?? null,
            side: assignment.side ?? null,
            platform: assignment.platform,
            pourDate: target,
            quantity: remainder,
            sequence: this.nextSequence(assignment.platform ?? "", target),
            // Explicit: the server otherwise carries a first placement's count.
            piecesProduced: 0,
          });
      this.absorbAssignments(Array.isArray(written) ? written : [written]);
      return true;
    } catch (error) {
      // Give the pieces back to the day they came from, so a failed shift loses
      // nothing from the schedule.
      try {
        const restored = await this.scheduleAssignmentsService.patch(assignment.id, {
          quantity: assignment.quantity,
        });
        this.absorbAssignments(Array.isArray(restored) ? restored : [restored]);
      } catch {
        /* the error below already says the shift failed */
      }
      this.reportScheduleError(error, "Failed to shift the uncast pieces");
      return false;
    }
  };

  // The same move in the other direction. Creede: "It's the same logic going
  // forward and backwards" — dragging a form back a day pulls the following
  // scheduled pieces in that form back with it.
  //
  // NEAREST-DAY-FIRST here, the mirror of shiftDay's ordering: the earliest
  // piece must vacate its day before the one behind it can occupy it.
  //
  // A one-day nudge on purpose, not an arbitrary date picker — Creede asked to
  // trial exactly this first: "let's try just the function of moving a piece
  // back one day and see how I like that."
  shiftDayBack = async (
    platform: string,
    fromDate: string,
    skipSaturdays = false
  ): Promise<boolean> => {
    const from = toDateOnly(fromDate);
    const chain = this.shiftChain(platform, from).sort((a, b) =>
      a.pourDate.localeCompare(b.pourDate)
    );
    if (chain.length === 0) return true;

    let ok = true;
    for (const assignment of chain) {
      const moved = await this.moveAssignment(
        assignment.id,
        platform,
        previousPourDay(assignment.pourDate, this.closedDayKeys, skipSaturdays)
      );
      ok = moved && ok;
    }
    return ok;
  };

  // ========================================
  // RESET
  // ========================================

  resetToDefaults = () => {
    runInAction(() => {
      this.forms = [];
      this.ticketGroups = [];
      this.productionItems = [];
      this.expandedGroupIds = [];
      this.selectedProjectId = null;
      this.selectedFormId = null;
      this.searchTerm = "";
      this.error = null;
    });
    this.loadData();
  };
}

export default MMProductionStore;
